import React, { useState, useEffect, useMemo } from 'react';
import {
  Search,
  Calendar,
  MapPin,
  RefreshCw,
  Send,
  Plus,
  CheckCircle2,
  Clock,
  XCircle,
  ExternalLink,
  Copy,
  Check,
  Users,
  UserCheck,
  Edit2,
  Trash2,
  AlertTriangle,
  AlertCircle,
  X,
  FileText,
  Package,
  FileDown,
  History,
  ShieldCheck,
  ChevronDown,
  Building2,
  Loader2,
  Info,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from 'lucide-react';
import { agendamentosService, type HistoricoAgendamentoItem } from '../../services/agendamentos.service';
import { presencaService } from '../../services/presenca.service';
import type { AgendamentoHistoricoItemContract, ReagendarParcialItemContract } from '@sstexames/shared-types';
import { documentosService } from '../../services/documentos.service';
import { colaboradoresService } from '../../services/colaboradores.service';
import { resultadosService } from '../../services/resultados.service';
import { formatCPF } from '../../utils/formatters';
import { formatarNomeExameAmigavel } from '../../utils/exames.utils';
import { copyToClipboard } from '../../utils/clipboard';
import { getHojeISO, getDeslocamentoDiasISO, getPrimeiroDiaMesISO, getUltimoDiaMesISO } from '../../utils/dateUtils';
import {
  clinicasService,
  type EstadoItem,
  type CidadeItem,
  type BairroItem,
  type ClinicaItem,
} from '../../services/clinicas.service';

interface HistoricoAgendamentosProps {
  onNovoAgendamento: () => void;
  onStatsChange?: (stats: {
    total: number;
    autoagendamentos: number;
    rh: number;
    candidatos: number;
    concluidos: number;
    expirados?: number;
  }) => void;
}

export default function HistoricoAgendamentos({ onNovoAgendamento, onStatsChange }: HistoricoAgendamentosProps) {
  const [agendamentos, setAgendamentos] = useState<HistoricoAgendamentoItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'futuros' | 'autoagendamento' | 'rh' | 'candidatos' | 'concluidos' | 'expirados' | 'todos'>('todos');
  const [reenviandoToken, setReenviandoToken] = useState<string | null>(null);
  const [copiedToken, setCopiedToken] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  // Estados de filtro de período (Data Inicial e Final)
  // Por padrão: Este Mês (primeiro dia ao último dia do mês atual) ancorado no fuso de SP
  const [dataInicio, setDataInicio] = useState<string>(getPrimeiroDiaMesISO());
  const [dataFim, setDataFim] = useState<string>(getUltimoDiaMesISO());
  const [periodoPreset, setPeriodoPreset] = useState<'proximos_30' | 'proximos_7' | 'este_mes' | 'hoje' | 'todos' | 'personalizado'>('este_mes');

  // Estados dos modais de Reagendamento e Cancelamento
  const [cancelandoItem, setCancelandoItem] = useState<HistoricoAgendamentoItem | null>(null);
  const [motivoCancelamento, setMotivoCancelamento] = useState('');
  const [reagendandoItem, setReagendandoItem] = useState<HistoricoAgendamentoItem | null>(null);
  const [novaDataReagendamento, setNovaDataReagendamento] = useState('');
  const [datasDisponiveisReagendar, setDatasDisponiveisReagendar] = useState<string[]>([]);
  const [loadingDatasReagendar, setLoadingDatasReagendar] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [downloadingDoc, setDownloadingDoc] = useState<Record<string, boolean>>({});
  const [novaClinicaReagendamento, setNovaClinicaReagendamento] = useState<number | null>(null);
  const [alterandoClinica, setAlterandoClinica] = useState<boolean>(false);
  const [clinicaSelecionadaInfo, setClinicaSelecionadaInfo] = useState<{
    id: number;
    nome: string;
    localizacao?: string;
  } | null>(null);
  const [filtroEstado, setFiltroEstado] = useState<string>('');
  const [filtroCidade, setFiltroCidade] = useState<string>('');
  const [filtroBairro, setFiltroBairro] = useState<string>('');
  const [estadosList, setEstadosList] = useState<EstadoItem[]>([]);
  const [cidadesList, setCidadesList] = useState<CidadeItem[]>([]);
  const [bairrosList, setBairrosList] = useState<BairroItem[]>([]);
  const [clinicasFiltradasList, setClinicasFiltradasList] = useState<ClinicaItem[]>([]);
  const [carregandoEstados, setCarregandoEstados] = useState<boolean>(false);
  const [carregandoCidades, setCarregandoCidades] = useState<boolean>(false);
  const [carregandoBairros, setCarregandoBairros] = useState<boolean>(false);
  const [carregandoClinicasFiltradas, setCarregandoClinicasFiltradas] = useState<boolean>(false);
  const [tipoReagendamento, setTipoReagendamento] = useState<'total' | 'parcial'>('total');
  const [itensReagendamento, setItensReagendamento] = useState<ReagendarParcialItemContract[]>([]);
  const [itensSelecionadosReagendar, setItensSelecionadosReagendar] = useState<number[]>([]);
  const [carregandoItensReagendamento, setCarregandoItensReagendamento] = useState(false);
  const [motivoReagendamento, setMotivoReagendamento] = useState('');
  const [subAgendamentosExpandidosCustomer, setSubAgendamentosExpandidosCustomer] = useState<Record<number, boolean>>({});

  const toggleSubAgendamentosCustomer = (id: number) => {
    setSubAgendamentosExpandidosCustomer((prev) => ({
      ...prev,
      [id]: prev[id] === undefined ? false : !prev[id],
    }));
  };

  // Estados e Handlers do Modal de Reagendamento de Autoagendamento (Expirados ou Renovação de Prazo)
  const [reagendandoExpiradoItem, setReagendandoExpiradoItem] = useState<HistoricoAgendamentoItem | null>(null);
  const [novaDataExpirado, setNovaDataExpirado] = useState<string>('');
  const [motivoExpirado, setMotivoExpirado] = useState<string>('');
  const [loadingReagendarExpirado, setLoadingReagendarExpirado] = useState<boolean>(false);

  const handleAbrirReagendarExpirado = (item: HistoricoAgendamentoItem) => {
    setReagendandoExpiradoItem(item);
    const dt = new Date();
    dt.setDate(dt.getDate() + 7);
    setNovaDataExpirado(dt.toISOString().split('T')[0]);
    setMotivoExpirado('');
  };

  const handleExecutarReagendarExpirado = async () => {
    if (!reagendandoExpiradoItem) return;
    if (!novaDataExpirado) {
      setFeedback({ type: 'error', message: 'Informe a nova data limite para o agendamento.' });
      return;
    }
    const hoje = new Date().toISOString().split('T')[0];
    if (novaDataExpirado < hoje) {
      setFeedback({ type: 'error', message: 'A nova data deve ser igual ou posterior a hoje.' });
      return;
    }

    try {
      setLoadingReagendarExpirado(true);
      setFeedback(null);

      const token = reagendandoExpiradoItem.token_escolha_clinica;
      let res: { success: boolean; message: string };

      if (token) {
        res = await agendamentosService.reagendarAutoagendamento(token, novaDataExpirado, motivoExpirado);
      } else {
        const targetId = reagendandoExpiradoItem.id || (reagendandoExpiradoItem.agendamento_ids && reagendandoExpiradoItem.agendamento_ids[0]);
        res = await agendamentosService.reagendarAgendamentoRH(targetId, novaDataExpirado, undefined, undefined, motivoExpirado);
      }

      setFeedback({
        type: 'success',
        message: res.message || 'Autoagendamento reagendado com sucesso! Um novo lembrete com link foi enviado ao colaborador por E-mail e WhatsApp.',
      });
      setReagendandoExpiradoItem(null);
      await carregarHistorico();
    } catch (err: unknown) {
      const errorObj = err as { response?: { data?: { message?: string } } };
      setFeedback({
        type: 'error',
        message: errorObj?.response?.data?.message || 'Erro ao reagendar autoagendamento.',
      });
    } finally {
      setLoadingReagendarExpirado(false);
    }
  };

  // Estados do Modal de Auditoria do Histórico de Status
  const [historicoAuditItem, setHistoricoAuditItem] = useState<HistoricoAgendamentoItem | null>(null);
  const [historicoStatusLista, setHistoricoStatusLista] = useState<AgendamentoHistoricoItemContract[]>([]);
  const [loadingHistoricoStatus, setLoadingHistoricoStatus] = useState(false);
  const [selectedComprovante, setSelectedComprovante] = useState<HistoricoAgendamentoItem | null>(null);
  const [selfieBlobUrl, setSelfieBlobUrl] = useState<string | null>(null);
  const [loadingSelfie, setLoadingSelfie] = useState(false);
  const [selfieError, setSelfieError] = useState(false);

  // Carregar selfie via Blob autenticado para visualização segura no modal de comprovante
  useEffect(() => {
    let isMounted = true;
    let currentBlobUrl: string | null = null;

    if (selectedComprovante && selectedComprovante.confirmou_presenca) {
      if (
        selectedComprovante.selfie_path &&
        (selectedComprovante.selfie_path.startsWith('data:') ||
          selectedComprovante.selfie_path.startsWith('blob:') ||
          selectedComprovante.selfie_path.startsWith('http://') ||
          selectedComprovante.selfie_path.startsWith('https://'))
      ) {
        setSelfieBlobUrl(selectedComprovante.selfie_path);
        setSelfieError(false);
        setLoadingSelfie(false);
        return;
      }
      const targetIdOrToken =
        selectedComprovante.token_presenca ||
        selectedComprovante.token_escolha_clinica ||
        selectedComprovante.id;
      if (targetIdOrToken) {
        setLoadingSelfie(true);
        setSelfieError(false);
        presencaService
          .getSelfieBlob(targetIdOrToken)
          .then((blob) => {
            if (isMounted && blob && blob.size > 0) {
              currentBlobUrl = URL.createObjectURL(blob);
              setSelfieBlobUrl(currentBlobUrl);
            } else if (isMounted) {
              setSelfieError(true);
            }
          })
          .catch((err) => {
            console.warn('Erro ao carregar selfie via Blob:', err);
            if (isMounted) setSelfieError(true);
          })
          .finally(() => {
            if (isMounted) setLoadingSelfie(false);
          });
      }
    } else {
      setSelfieBlobUrl(null);
      setSelfieError(false);
      setLoadingSelfie(false);
    }

    return () => {
      isMounted = false;
      if (currentBlobUrl) {
        URL.revokeObjectURL(currentBlobUrl);
      }
    };
  }, [selectedComprovante]);

  const handleAbrirReagendar = async (item: HistoricoAgendamentoItem) => {
    setReagendandoItem(item);
    setTipoReagendamento('total');
    setItensSelecionadosReagendar([]);
    setMotivoReagendamento('');
    const targetClinicaId = item.clinica_id ? Number(item.clinica_id) : null;
    setNovaClinicaReagendamento(targetClinicaId);
    setClinicaSelecionadaInfo(
      targetClinicaId
        ? {
            id: targetClinicaId,
            nome: item.clinica_nome || 'Clínica Credenciada',
            localizacao: item.clinica_localizacao || '',
          }
        : null
    );
    setAlterandoClinica(false);
    setFiltroEstado('');
    setFiltroCidade('');
    setFiltroBairro('');
    setClinicasFiltradasList([]);

    const dataAtual = item.data_agendamento ? item.data_agendamento.substring(0, 10) : '';
    const dataMinima = getDeslocamentoDiasISO(0);
    setNovaDataReagendamento(dataAtual >= dataMinima ? dataAtual : '');
    setDatasDisponiveisReagendar([]);

    if (targetClinicaId) {
      setLoadingDatasReagendar(true);
      try {
        const datas = await clinicasService.getProximasDatasDisponiveis(targetClinicaId, 60);
        const datasLimpas = (datas || []).map((d) => {
          if (d.includes('/')) {
            const [dia, mes, ano] = d.split('/');
            return `${ano}-${mes}-${dia}`;
          }
          return d.includes('T') ? d.split('T')[0] : d;
        });
        setDatasDisponiveisReagendar(datasLimpas);
        // Se a data atual não for futura ou não estiver nas disponíveis, sugere a primeira data válida futura
        if (datasLimpas.length > 0 && (!dataAtual || dataAtual < dataMinima)) {
          const primeiraValida = datasLimpas.find((d) => d >= dataMinima);
          if (primeiraValida) {
            setNovaDataReagendamento(primeiraValida);
          }
        }
      } catch (err) {
        console.error('Erro ao consultar datas disponíveis da clínica:', err);
      } finally {
        setLoadingDatasReagendar(false);
      }
    }

    // Carregar exames elegíveis para sub-agendamento / reagendamento pontual
    setCarregandoItensReagendamento(true);
    try {
      const resp = await agendamentosService.obterItensReagendamento(item.id);
      const itens = Array.isArray(resp) ? resp : (resp?.itens || []);
      setItensReagendamento(itens);
      setItensSelecionadosReagendar(itens.map((i) => i.id));
    } catch (err) {
      console.warn('Erro ao consultar exames do agendamento para reagendamento parcial:', err);
      setItensReagendamento([]);
      setItensSelecionadosReagendar([]);
    } finally {
      setCarregandoItensReagendamento(false);
    }
  };

  const carregarEstados = async () => {
    if (estadosList.length > 0) return;
    setCarregandoEstados(true);
    try {
      const data = await clinicasService.getEstados();
      setEstadosList(data || []);
    } catch (err) {
      console.error('Erro ao carregar estados para reagendamento:', err);
    } finally {
      setCarregandoEstados(false);
    }
  };

  const handleSelecionarEstado = async (uf: string) => {
    setFiltroEstado(uf);
    setFiltroCidade('');
    setFiltroBairro('');
    setCidadesList([]);
    setBairrosList([]);
    setClinicasFiltradasList([]);
    if (!uf) return;

    setCarregandoCidades(true);
    setCarregandoClinicasFiltradas(true);
    try {
      const [cidades, clinicas] = await Promise.all([
        clinicasService.getCidadesByEstado(uf),
        clinicasService.getClinicasFiltradas({ estado: uf }),
      ]);
      setCidadesList(cidades || []);
      setClinicasFiltradasList(clinicas || []);
    } catch (err) {
      console.error('Erro ao carregar cidades/clínicas do estado:', err);
    } finally {
      setCarregandoCidades(false);
      setCarregandoClinicasFiltradas(false);
    }
  };

  const handleSelecionarCidade = async (cidade: string) => {
    setFiltroCidade(cidade);
    setFiltroBairro('');
    setBairrosList([]);
    if (!cidade) {
      if (filtroEstado) {
        setCarregandoClinicasFiltradas(true);
        try {
          const clinicas = await clinicasService.getClinicasFiltradas({ estado: filtroEstado });
          setClinicasFiltradasList(clinicas || []);
        } finally {
          setCarregandoClinicasFiltradas(false);
        }
      }
      return;
    }

    setCarregandoBairros(true);
    setCarregandoClinicasFiltradas(true);
    try {
      const [bairros, clinicas] = await Promise.all([
        clinicasService.getBairrosByCidadeEstado(filtroEstado, cidade),
        clinicasService.getClinicasFiltradas({ estado: filtroEstado, cidade }),
      ]);
      setBairrosList(bairros || []);
      setClinicasFiltradasList(clinicas || []);
    } catch (err) {
      console.error('Erro ao carregar bairros/clínicas da cidade:', err);
    } finally {
      setCarregandoBairros(false);
      setCarregandoClinicasFiltradas(false);
    }
  };

  const handleSelecionarBairro = async (bairro: string) => {
    setFiltroBairro(bairro);
    setCarregandoClinicasFiltradas(true);
    try {
      const clinicas = await clinicasService.getClinicasFiltradas({
        estado: filtroEstado,
        cidade: filtroCidade,
        bairro: bairro || undefined,
      });
      setClinicasFiltradasList(clinicas || []);
    } catch (err) {
      console.error('Erro ao filtrar clínicas por bairro:', err);
    } finally {
      setCarregandoClinicasFiltradas(false);
    }
  };

  const handleTrocaClinica = async (novaClinicaId: number | null) => {
    setNovaClinicaReagendamento(novaClinicaId);
    if (!novaClinicaId) return;
    setLoadingDatasReagendar(true);
    try {
      const datas = await clinicasService.getProximasDatasDisponiveis(novaClinicaId, 60);
      const datasLimpas = (datas || []).map((d) => {
        if (d.includes('/')) {
          const [dia, mes, ano] = d.split('/');
          return `${ano}-${mes}-${dia}`;
        }
        return d.includes('T') ? d.split('T')[0] : d;
      });
      setDatasDisponiveisReagendar(datasLimpas);
      const dataMinima = getDeslocamentoDiasISO(0);
      const primeiraValida = datasLimpas.find((d) => d >= dataMinima);
      if (primeiraValida) {
        setNovaDataReagendamento(primeiraValida);
      }
    } catch (err) {
      console.error('Erro ao consultar datas da clínica selecionada:', err);
    } finally {
      setLoadingDatasReagendar(false);
    }
  };

  const handleEscolherClinica = (clinica: ClinicaItem) => {
    setNovaClinicaReagendamento(clinica.id);
    setClinicaSelecionadaInfo({
      id: clinica.id,
      nome: clinica.nome,
      localizacao: [clinica.bairro, clinica.cidade, clinica.uf].filter(Boolean).join(' - ') || clinica.endereco,
    });
    setAlterandoClinica(false);
    handleTrocaClinica(clinica.id);
  };

  const restaurarClinicaOriginal = () => {
    if (!reagendandoItem) return;
    const targetClinicaId = reagendandoItem.clinica_id ? Number(reagendandoItem.clinica_id) : null;
    setNovaClinicaReagendamento(targetClinicaId);
    setClinicaSelecionadaInfo(
      targetClinicaId
        ? {
            id: targetClinicaId,
            nome: reagendandoItem.clinica_nome || 'Clínica Original',
            localizacao: reagendandoItem.clinica_localizacao || '',
          }
        : null
    );
    setAlterandoClinica(false);
    if (targetClinicaId) {
      handleTrocaClinica(targetClinicaId);
    }
  };

  const handleAbrirHistoricoStatus = async (item: HistoricoAgendamentoItem) => {
    setHistoricoAuditItem(item);
    setLoadingHistoricoStatus(true);
    try {
      const targetId = item.id || (item.agendamento_ids && item.agendamento_ids[0]);
      if (!targetId) {
        setHistoricoStatusLista([]);
        return;
      }
      const data = await presencaService.getHistorico(targetId);
      setHistoricoStatusLista(data);
    } catch {
      setHistoricoStatusLista([]);
    } finally {
      setLoadingHistoricoStatus(false);
    }
  };

  const handleDownload = async (tipo: 'guia' | 'kit-exame', item: HistoricoAgendamentoItem) => {
    const targetId = item.id || item.pedido_id;
    const key = `${tipo}_${targetId}_${item.colaborador_id}`;
    if (downloadingDoc[key]) return;

    try {
      setDownloadingDoc((prev) => ({ ...prev, [key]: true }));
      const codigo = btoa(`${targetId}_${item.colaborador_id}`);
      const isCancelado =
        item.status?.toLowerCase() === 'cancelado' ||
        item.status_operacional === 'CANCELADO' ||
        item.status_nome?.toLowerCase() === 'cancelado';
      const isReagendado = !isCancelado && Boolean(item.agendamento_original);
      const suffix = isCancelado ? '_cancelado' : isReagendado ? '_reagendado' : '';
      const nomeColab = (item.colaborador_nome || 'colaborador').replace(/\s+/g, '_');

      if (tipo === 'guia') {
        const blob = await documentosService.downloadGuia(codigo);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `Guia-${nomeColab}-${targetId}${suffix}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } else {
        const blob = await documentosService.downloadKitExame(codigo);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `KitExame-${nomeColab}-${targetId}${suffix}.pdf`);
        link.setAttribute('target', '_blank');
        document.body.appendChild(link);
        link.click();
        link.remove();
        setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      }
    } catch (err: unknown) {
      console.error('Erro ao baixar documento:', err);
      setFeedback({ message: 'Erro ao gerar documento para download.', type: 'error' });
    } finally {
      setDownloadingDoc((prev) => ({ ...prev, [key]: false }));
    }
  };

  const [downloadingAsoId, setDownloadingAsoId] = useState<number | null>(null);

  const handleDownloadAso = async (
    colaboradorId: number,
    nome: string,
    resultadoId?: number | null,
    agendamentoId?: number,
  ) => {
    try {
      setDownloadingAsoId(colaboradorId);
      let blob: Blob;
      if (agendamentoId) {
        try {
          blob = await agendamentosService.downloadAso(agendamentoId);
        } catch (agErr) {
          console.warn('Falha no endpoint direto de ASO do agendamento, tentando fallbacks:', agErr);
          if (resultadoId) {
            blob = await resultadosService.downloadResultado(resultadoId);
          } else {
            blob = await colaboradoresService.downloadColaboradorAso(colaboradorId);
          }
        }
      } else if (resultadoId) {
        blob = await resultadosService.downloadResultado(resultadoId);
      } else {
        blob = await colaboradoresService.downloadColaboradorAso(colaboradorId);
      }
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const cleanName = (nome || 'colaborador').replace(/[^a-zA-Z0-9]/g, '_');
      a.download = `ASO_${cleanName}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      setFeedback({ message: 'Download do ASO concluído com sucesso!', type: 'success' });
    } catch (err: unknown) {
      console.error('Erro ao baixar ASO:', err);
      if (resultadoId) {
        try {
          const blob = await colaboradoresService.downloadColaboradorAso(colaboradorId);
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          const cleanName = (nome || 'colaborador').replace(/[^a-zA-Z0-9]/g, '_');
          a.download = `ASO_${cleanName}.pdf`;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
          setFeedback({ message: 'Download do ASO concluído com sucesso!', type: 'success' });
          return;
        } catch (e) {
          console.error('Fallback downloadColaboradorAso falhou:', e);
        }
      }
      setFeedback({ message: 'Não foi possível realizar o download do ASO no momento.', type: 'error' });
    } finally {
      setDownloadingAsoId(null);
    }
  };

  useEffect(() => {
    carregarHistorico();
  }, []);

  const carregarHistorico = async () => {
    try {
      setLoading(true);
      const data = await agendamentosService.getHistorico();
      setAgendamentos(data);
    } catch (err: unknown) {
      console.error('Erro ao carregar histórico:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleReenviarLembrete = async (token: string) => {
    try {
      setReenviandoToken(token);
      setFeedback(null);
      const res = await agendamentosService.reenviarLembreteAutoagendamento(token);
      setFeedback({ message: res.message || 'Lembrete reenviado com sucesso ao colaborador!', type: 'success' });
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } } };
      setFeedback({
        message: error?.response?.data?.message || 'Erro ao reenviar lembrete.',
        type: 'error',
      });
    } finally {
      setReenviandoToken(null);
    }
  };

  const handleCopyLink = async (token: string) => {
    const url = `${window.location.origin}/escolha-clinica/${token}`;
    const ok = await copyToClipboard(url);
    if (ok) {
      setCopiedToken(token);
      setTimeout(() => setCopiedToken(null), 2500);
    } else {
      setFeedback({
        message: `Não foi possível copiar automaticamente para a área de transferência. Link: ${url}`,
        type: 'error',
      });
    }
  };

  const handleConfirmCancelar = async () => {
    if (!cancelandoItem) return;
    try {
      setActionLoading(true);
      setFeedback(null);
      let res: { success: boolean; message: string };
      if (cancelandoItem.escolha_clinica_tipo === 'colaborador' && cancelandoItem.token_escolha_clinica) {
        res = await agendamentosService.cancelarAutoagendamento(cancelandoItem.token_escolha_clinica);
      } else {
        res = await agendamentosService.cancelarAgendamentoRH(cancelandoItem.id, motivoCancelamento);
      }
      setFeedback({ message: res.message || 'Agendamento cancelado com sucesso. A clínica credenciada foi notificada.', type: 'success' });
      setCancelandoItem(null);
      setMotivoCancelamento('');
      await carregarHistorico();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } } };
      setFeedback({
        message: error?.response?.data?.message || 'Erro ao cancelar o agendamento.',
        type: 'error',
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleConfirmReagendar = async () => {
    if (!reagendandoItem || !novaDataReagendamento) return;
    const dataMinima = getDeslocamentoDiasISO(0);
    if (novaDataReagendamento < dataMinima) {
      setFeedback({
        message: 'A nova data para o reagendamento não pode ser no passado.',
        type: 'error',
      });
      return;
    }
    try {
      setActionLoading(true);
      setFeedback(null);

      const isParcial =
        itensReagendamento.length > 1 &&
        itensSelecionadosReagendar.length > 0 &&
        itensSelecionadosReagendar.length < itensReagendamento.length;

      if (itensReagendamento.length > 0 && itensSelecionadosReagendar.length === 0) {
        setFeedback({
          message: 'Selecione ao menos um exame para realizar o reagendamento.',
          type: 'error',
        });
        setActionLoading(false);
        return;
      }

      if (isParcial) {
        const res = await agendamentosService.reagendarParcial(reagendandoItem.id, {
          agendamento_id: reagendandoItem.id,
          nova_data: novaDataReagendamento,
          nova_clinica_id: novaClinicaReagendamento ? Number(novaClinicaReagendamento) : undefined,
          motivo: motivoReagendamento || 'Reagendamento pontual de exames',
          itens_ids: itensSelecionadosReagendar,
        });

        setFeedback({
          message: `Sub-agendamento #${res.sub_agendamento_id} criado com sucesso para ${res.itens_reagendados} exame(s)!`,
          type: 'success',
        });
      } else {
        const res = await agendamentosService.reagendarAgendamentoRH(
          reagendandoItem.id,
          novaDataReagendamento,
          undefined,
          novaClinicaReagendamento ? Number(novaClinicaReagendamento) : undefined,
          motivoReagendamento || undefined,
        );
        setFeedback({ message: res.message || 'Agendamento reagendado com sucesso.', type: 'success' });
      }

      setReagendandoItem(null);
      setNovaClinicaReagendamento(null);
      setNovaDataReagendamento('');
      setItensSelecionadosReagendar([]);
      setMotivoReagendamento('');
      await carregarHistorico();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } } };
      setFeedback({
        message: error?.response?.data?.message || 'Erro ao reagendar o agendamento.',
        type: 'error',
      });
    } finally {
      setActionLoading(false);
    }
  };

  // Helper para formatação de data brasileira (DD/MM/YYYY) imune a desvios de timezone UTC
  const formatDateBR = (dateStr?: string | null) => {
    if (!dateStr) return '';
    const clean = dateStr.includes('T') ? dateStr.split('T')[0] : dateStr.substring(0, 10);
    const parts = clean.split('-');
    if (parts.length === 3) {
      return `${parts[2]}/${parts[1]}/${parts[0]}`;
    }
    return dateStr;
  };

  const formatDateTimeBR = (dateStr?: string | null) => {
    if (!dateStr) return '';
    try {
      const d = new Date(dateStr);
      return d.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateStr;
    }
  };

  // Helper para verificar se um agendamento é futuro ou pendente de realização (sem desvios de timezone)
  const isFuturoOuPendente = (item: HistoricoAgendamentoItem) => {
    // Exames cancelados ou não comparecidos não são futuros/pendentes
    if (item.status_badge === 'red' || item.status === 'Cancelado' || item.status === 'Não Compareceu') return false;
    // Exames concluídos já foram finalizados com laudo ASO
    if (item.has_aso || (item.status === 'Realizado' && item.status_badge === 'green') || item.status === 'Concluído') return false;
    // Agendamentos em compareceu, auditoria, duplo check ou aguardando resultado continuam pendentes de emissão para o cliente
    if (
      item.status === 'Compareceu' ||
      item.status === 'Presente' ||
      item.status === 'Em Auditoria' ||
      item.status === 'Duplo Check' ||
      item.status === 'Aguardando Resultado' ||
      item.confirmou_presenca
    ) {
      return true;
    }
    // Autoagendamentos sem data ainda são pendentes de escolha pelo colaborador
    if (!item.data_agendamento) return true;
    const itemDateStr = item.data_agendamento.includes('T')
      ? item.data_agendamento.split('T')[0]
      : item.data_agendamento.substring(0, 10);
    const hojeStr = getHojeISO();
    // Agendamentos de hoje ou futuros (incluindo confirmados ou com presença já registrada hoje)
    return itemDateStr >= hojeStr;
  };

  // Helper para verificar se o agendamento está dentro do período selecionado
  const matchesDateRange = (item: HistoricoAgendamentoItem): boolean => {
    if (periodoPreset === 'todos' || (!dataInicio && !dataFim)) {
      return true;
    }

    if (item.data_agendamento) {
      const itemDate = item.data_agendamento.includes('T')
        ? item.data_agendamento.split('T')[0]
        : item.data_agendamento.substring(0, 10);
      if (dataInicio && itemDate < dataInicio) return false;
      if (dataFim && itemDate > dataFim) return false;
      return true;
    }

    // Se ainda não tem data definida (ex: link enviado aguardando colaborador escolher clínica/dia)
    // Nos presets de agendamento futuro (hoje, próximos 7, próximos 30 dias, este mês), exibe pois é pendente
    if (periodoPreset === 'proximos_30' || periodoPreset === 'proximos_7' || periodoPreset === 'hoje' || periodoPreset === 'este_mes') {
      return item.status_badge !== 'red';
    }

    // Em período personalizado, filtra pela data de criação/emissão
    if (item.created_at) {
      const itemCreated = item.created_at.includes('T')
        ? item.created_at.split('T')[0]
        : item.created_at.substring(0, 10);
      if (dataInicio && itemCreated < dataInicio) return false;
      if (dataFim && itemCreated > dataFim) return false;
    }
    return true;
  };

  // Helper para obter a data limite/prazo de expiração do autoagendamento (emissão + 5 dias se data_agendamento for null)
  const getPrazoItem = (item: HistoricoAgendamentoItem): string => {
    if (item.data_agendamento) return item.data_agendamento;
    const d = new Date(item.created_at);
    d.setDate(d.getDate() + 5);
    return d.toISOString();
  };

  // Helper para verificar se o autoagendamento está expirado (link não acessado/sem clínica após a data limite)
  const isExpirado = (item: HistoricoAgendamentoItem): boolean => {
    if (item.status_badge === 'red' || item.status === 'Cancelado') return false;
    if (item.has_aso || (item.status === 'Realizado' && item.status_badge === 'green') || item.status === 'Concluído') return false;
    if (
      item.status === 'Compareceu' ||
      item.status === 'Presente' ||
      item.status === 'Em Auditoria' ||
      item.status === 'Duplo Check' ||
      item.status === 'Aguardando Resultado' ||
      item.confirmou_presenca
    ) {
      return false;
    }
    const prazo = getPrazoItem(item);
    const itemDateStr = prazo.includes('T')
      ? prazo.split('T')[0]
      : prazo.substring(0, 10);
    const hojeStr = getHojeISO();
    if (itemDateStr >= hojeStr) return false;

    const isPendenteColaborador =
      item.escolha_clinica_tipo === 'colaborador' &&
      (!item.clinica_id ||
        !item.clinica_nome ||
        item.clinica_nome === '-' ||
        item.clinica_nome.toLowerCase().includes('definir') ||
        item.clinica_nome.toLowerCase().includes('pendente'));

    return isPendenteColaborador || !item.clinica_id;
  };

  // Helper para verificar se um agendamento concluído está dentro do período selecionado
  const matchesConcluidosDateRange = (item: HistoricoAgendamentoItem): boolean => {
    const ehConcluido = item.status_badge === 'green' || item.status === 'Realizado' || item.status === 'Concluído';
    if (!ehConcluido) return false;

    if (periodoPreset === 'todos' || (!dataInicio && !dataFim)) {
      return true;
    }

    if (item.data_agendamento) {
      const itemDate = item.data_agendamento.includes('T')
        ? item.data_agendamento.split('T')[0]
        : item.data_agendamento.substring(0, 10);

      // No preset padrão de 30 dias, exibe os concluídos dos últimos 30 dias até hoje (+30 dias)
      if (periodoPreset === 'proximos_30') {
        return itemDate >= getDeslocamentoDiasISO(-30) && itemDate <= getDeslocamentoDiasISO(30);
      }
      if (periodoPreset === 'proximos_7') {
        return itemDate >= getDeslocamentoDiasISO(-7) && itemDate <= getDeslocamentoDiasISO(7);
      }
      if (periodoPreset === 'hoje') {
        return itemDate === getHojeISO();
      }
      if (periodoPreset === 'este_mes') {
        return itemDate >= getPrimeiroDiaMesISO() && itemDate <= getUltimoDiaMesISO();
      }
      if (dataInicio && itemDate < dataInicio) return false;
      if (dataFim && itemDate > dataFim) return false;
      return true;
    }

    return true;
  };

  // Métricas sincronizadas com o standard do painel corporativo
  const totalNoPeriodo = agendamentos.filter((a) => matchesDateRange(a) || matchesConcluidosDateRange(a)).length;
  const totalFuturos = agendamentos.filter((a) => matchesDateRange(a) && isFuturoOuPendente(a)).length;
  const totalAuto = agendamentos.filter((a) => matchesDateRange(a) && isFuturoOuPendente(a) && a.escolha_clinica_tipo === 'colaborador').length;
  const totalRH = agendamentos.filter((a) => matchesDateRange(a) && isFuturoOuPendente(a) && a.escolha_clinica_tipo !== 'colaborador').length;
  const isCandidatoVisivel = (a: HistoricoAgendamentoItem) => {
    if (!a.is_candidato) return false;
    if (a.has_aso || a.status === 'Concluído' || (a.status === 'Realizado' && a.status_badge === 'green')) return false;
    return true;
  };

  const totalCandidatos = agendamentos.filter((a) => matchesDateRange(a) && isCandidatoVisivel(a)).length;
  const totalConcluidos = agendamentos.filter(matchesConcluidosDateRange).length;
  const totalExpirados = agendamentos.filter(isExpirado).length;

  // Notificar componente pai (Cadastro.tsx) com as métricas exatas calculadas
  useEffect(() => {
    if (onStatsChange) {
      onStatsChange({
        total: totalFuturos,
        autoagendamentos: totalAuto,
        rh: totalRH,
        candidatos: totalCandidatos,
        concluidos: totalConcluidos,
        expirados: totalExpirados,
      });
    }
  }, [totalFuturos, totalAuto, totalRH, totalCandidatos, totalConcluidos, totalExpirados, onStatsChange]);

  const filtered = agendamentos.filter((item) => {
    const rawSearch = search.trim();
    if (!rawSearch) {
      if (statusFilter === 'futuros') return matchesDateRange(item) && isFuturoOuPendente(item);
      if (statusFilter === 'candidatos') return matchesDateRange(item) && isCandidatoVisivel(item);
      if (statusFilter === 'autoagendamento') return matchesDateRange(item) && isFuturoOuPendente(item) && item.escolha_clinica_tipo === 'colaborador';
      if (statusFilter === 'rh') return matchesDateRange(item) && isFuturoOuPendente(item) && item.escolha_clinica_tipo !== 'colaborador';
      if (statusFilter === 'concluidos') return matchesConcluidosDateRange(item);
      if (statusFilter === 'expirados') return isExpirado(item);
      return matchesDateRange(item) || matchesConcluidosDateRange(item);
    }

    const qLower = rawSearch.toLowerCase();
    const qNorm = qLower.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    const qDigits = rawSearch.replace(/\D/g, '');

    const nome = (item.colaborador_nome || '').toLowerCase();
    const nomeNorm = nome.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

    const cpfDigits = (item.colaborador_cpf || '').replace(/\D/g, '');
    const codPedido = (item.codigo_pedido || (item as { pedido_codigo?: string }).pedido_codigo || '').toLowerCase();
    const clinica = (item.clinica_nome || '').toLowerCase();
    const clinicaNorm = clinica.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    const exame = (item.exame_nome || '').toLowerCase();
    const exameNorm = exame.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

    const matchNome = nome.includes(qLower) || nomeNorm.includes(qNorm);
    const matchCpf = qDigits.length > 0 && cpfDigits.includes(qDigits);
    const matchCod = codPedido.includes(qLower);
    const matchClinica = clinica.includes(qLower) || clinicaNorm.includes(qNorm);
    const matchExame = exame.includes(qLower) || exameNorm.includes(qNorm) || (
      Array.isArray(item.exames_clinicos) &&
      item.exames_clinicos.some((e: string) => {
        const eStr = (e || '').toLowerCase();
        return eStr.includes(qLower) || eStr.normalize('NFD').replace(/[\u0300-\u036f]/g, '').includes(qNorm);
      })
    );

    const matchesSearch = matchNome || matchCpf || matchCod || matchClinica || matchExame;
    if (!matchesSearch) return false;

    // Se o usuário digitou uma busca específica por nome, CPF ou código de pedido, prioriza a busca textual em todo o histórico
    return true;
  });

  // Estrutura em Árvore (Tree View): Agendamentos Mestres agregam seus Sub-agendamentos como nós filhos
  const arvoreCustomer = useMemo(() => {
    const mapaPorId = new Map<number, HistoricoAgendamentoItem>();
    filtered.forEach((ag) => {
      const id = ag.id || (ag.agendamento_ids && ag.agendamento_ids[0]);
      if (id) mapaPorId.set(id, ag);
    });

    const filhosPorPaiId = new Map<number, HistoricoAgendamentoItem[]>();
    filtered.forEach((ag) => {
      if (ag.is_sub_agendamento && ag.agendamento_pai_id) {
        if (!filhosPorPaiId.has(ag.agendamento_pai_id)) {
          filhosPorPaiId.set(ag.agendamento_pai_id, []);
        }
        filhosPorPaiId.get(ag.agendamento_pai_id)!.push(ag);
      }
    });

    const mestres: Array<HistoricoAgendamentoItem & { sub_agendamentos_detalhados?: HistoricoAgendamentoItem[] }> = [];
    filtered.forEach((ag) => {
      const id = ag.id || (ag.agendamento_ids && ag.agendamento_ids[0]);
      if (!ag.is_sub_agendamento) {
        const filhos = id ? (filhosPorPaiId.get(id) || []) : [];
        mestres.push({
          ...ag,
          sub_agendamentos_detalhados: filhos,
        });
      } else if (!ag.agendamento_pai_id || !mapaPorId.has(ag.agendamento_pai_id)) {
        // Sub-agendamento cujo pai não consta no filtro atual permanece visível
        mestres.push({
          ...ag,
          sub_agendamentos_detalhados: [],
        });
      }
    });

    return mestres;
  }, [filtered]);

  // Paginação inteligente dos agendamentos
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(25);

  // Reseta para a primeira página sempre que filtros de busca, status ou período mudarem
  useEffect(() => {
    setCurrentPage(1);
  }, [search, statusFilter, dataInicio, dataFim, periodoPreset]);

  const totalMestres = arvoreCustomer.length;
  const totalPages = Math.max(1, Math.ceil(totalMestres / pageSize));
  const paginatedArvoreCustomer = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return arvoreCustomer.slice(start, start + pageSize);
  }, [arvoreCustomer, currentPage, pageSize]);

  return (
    <div className="space-y-6">
      {/* Top Action & Title Bar */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <span className="text-[11px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-md border border-emerald-100">
            Acompanhamento em Tempo Real
          </span>
          <h2 className="text-xl font-bold text-gray-900 mt-2 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-emerald-600" />
            Agendamentos e Autoagendamentos Realizados
          </h2>
          <p className="text-xs text-gray-500 mt-1">
            Consulte e gerencie todos os exames emitidos: agendamentos diretos do RH (com data e clínica já definidas) e links de autoagendamento enviados aos colaboradores.
          </p>
        </div>
        <div className="flex items-center gap-2.5">
          <button
            onClick={carregarHistorico}
            disabled={loading}
            className="p-2.5 text-gray-600 hover:text-emerald-700 bg-gray-50 hover:bg-gray-100 rounded-xl border border-gray-200 transition-colors"
            title="Atualizar lista"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={onNovoAgendamento}
            className="flex items-center gap-2 px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-xl shadow-sm transition-all transform active:scale-95"
          >
            <Plus className="w-4 h-4" />
            <span>Novo Agendamento</span>
          </button>
        </div>
      </div>

      {/* Metric Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <div
          onClick={() => setStatusFilter('futuros')}
          className={`p-4 rounded-xl border cursor-pointer transition-all ${
            statusFilter === 'futuros'
              ? 'bg-white border-emerald-500 shadow-md ring-2 ring-emerald-500/20'
              : 'bg-white border-gray-200 hover:border-gray-300 shadow-xs'
          }`}
        >
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-emerald-800">Futuros & Pendentes</span>
            <Calendar className="w-4 h-4 text-emerald-600" />
          </div>
          <p className="text-2xl font-black text-emerald-900 mt-1">{totalFuturos}</p>
          <p className="text-[11px] text-emerald-700/80 mt-0.5">Exames a realizar / aguardando</p>
        </div>

        <div
          onClick={() => setStatusFilter('rh')}
          className={`p-4 rounded-xl border cursor-pointer transition-all ${
            statusFilter === 'rh'
              ? 'bg-white border-purple-500 shadow-md ring-2 ring-purple-500/20'
              : 'bg-white border-gray-200 hover:border-gray-300 shadow-xs'
          }`}
        >
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-purple-700">Agendados pelo RH</span>
            <Users className="w-4 h-4 text-purple-600" />
          </div>
          <p className="text-2xl font-black text-purple-900 mt-1">{totalRH}</p>
          <p className="text-[11px] text-purple-600/80 mt-0.5">Clínica e data já agendadas</p>
        </div>

        <div
          onClick={() => setStatusFilter('autoagendamento')}
          className={`p-4 rounded-xl border cursor-pointer transition-all ${
            statusFilter === 'autoagendamento'
              ? 'bg-white border-blue-500 shadow-md ring-2 ring-blue-500/20'
              : 'bg-white border-gray-200 hover:border-gray-300 shadow-xs'
          }`}
        >
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-blue-700">Autoagendamentos</span>
            <ExternalLink className="w-4 h-4 text-blue-600" />
          </div>
          <p className="text-2xl font-black text-blue-900 mt-1">{totalAuto}</p>
          <p className="text-[11px] text-blue-600/80 mt-0.5">Links enviados a colaboradores</p>
        </div>

        <div
          onClick={() => setStatusFilter('expirados')}
          className={`p-4 rounded-xl border cursor-pointer transition-all ${
            statusFilter === 'expirados'
              ? 'bg-white border-rose-500 shadow-md ring-2 ring-rose-500/20'
              : 'bg-white border-gray-200 hover:border-gray-300 shadow-xs'
          }`}
        >
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-rose-700">Links Expirados</span>
            <AlertCircle className="w-4 h-4 text-rose-600" />
          </div>
          <p className="text-2xl font-black text-rose-900 mt-1">{totalExpirados}</p>
          <p className="text-[11px] text-rose-600/80 mt-0.5">Sem clínica após a data</p>
        </div>

        <div
          onClick={() => setStatusFilter('candidatos')}
          className={`p-4 rounded-xl border cursor-pointer transition-all ${
            statusFilter === 'candidatos'
              ? 'bg-white border-amber-500 shadow-md ring-2 ring-amber-500/20'
              : 'bg-white border-gray-200 hover:border-gray-300 shadow-xs'
          }`}
        >
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-amber-700">Candidatos</span>
            <UserCheck className="w-4 h-4 text-amber-600" />
          </div>
          <p className="text-2xl font-black text-amber-900 mt-1">{totalCandidatos}</p>
          <p className="text-[11px] text-amber-600/80 mt-0.5">Exames admissionais</p>
        </div>

        <div
          onClick={() => setStatusFilter('concluidos')}
          className={`p-4 rounded-xl border cursor-pointer transition-all ${
            statusFilter === 'concluidos'
              ? 'bg-white border-teal-500 shadow-md ring-2 ring-teal-500/20'
              : 'bg-white border-gray-200 hover:border-gray-300 shadow-xs'
          }`}
        >
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-teal-700">Concluídos</span>
            <CheckCircle2 className="w-4 h-4 text-teal-600" />
          </div>
          <p className="text-2xl font-black text-teal-900 mt-1">{totalConcluidos}</p>
          <p className="text-[11px] text-teal-600/80 mt-0.5">Laudos e ASOs emitidos</p>
        </div>
      </div>

      {/* Hint Informativo: Política Anti-Spam & Envio */}
      <div className="bg-sky-50/80 border border-sky-200 rounded-xl p-3.5 flex items-start gap-3 text-xs text-sky-900">
        <AlertCircle className="w-4 h-4 text-sky-600 shrink-0 mt-0.5" />
        <div className="space-y-0.5">
          <p className="font-semibold text-sky-950">Política de Envio de Lembretes e Preservação Anti-Spam</p>
          <p className="text-sky-800 text-[11px] leading-relaxed">
            Para exames <strong>demissionais</strong>, não são enviados lembretes diários automáticos por e-mail e WhatsApp, seguindo as diretrizes de preservação anti-spam e boas práticas da empresa.
            Além disso, assim que o colaborador escolhe a clínica e confirma seu horário, os lembretes diários são pausados imediatamente.
          </p>
        </div>
      </div>

      {feedback && (
        <div
          className={`p-4 rounded-xl text-xs font-medium flex items-center justify-between ${
            feedback.type === 'success'
              ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
              : 'bg-red-50 text-red-800 border border-red-200'
          }`}
        >
          <span>{feedback.message}</span>
          <button onClick={() => setFeedback(null)} className="text-xs font-bold underline ml-4">
            Fechar
          </button>
        </div>
      )}

      {/* Filter and Search Bar */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 space-y-4">
        {/* Seletor de Período / Datas (Padrão: Hoje até Hoje + 30 dias) */}
        <div className="bg-emerald-50/50 border border-emerald-100 rounded-xl p-3.5 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-bold text-gray-700 flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5 text-emerald-600" />
              Período de Agendamento:
            </span>
            <div className="flex items-center gap-1.5">
              <input
                type="date"
                value={dataInicio}
                onChange={(e) => {
                  setDataInicio(e.target.value);
                  setPeriodoPreset('personalizado');
                }}
                className="px-2.5 py-1 text-xs border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-emerald-500 font-medium text-gray-800"
                title="Data Inicial"
              />
              <span className="text-xs text-gray-400 font-medium">até</span>
              <input
                type="date"
                value={dataFim}
                onChange={(e) => {
                  setDataFim(e.target.value);
                  setPeriodoPreset('personalizado');
                }}
                className="px-2.5 py-1 text-xs border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-emerald-500 font-medium text-gray-800"
                title="Data Final"
              />
            </div>
          </div>

          {/* Presets Rápidos */}
          <div className="flex flex-wrap items-center gap-1.5">
            <button
              type="button"
              onClick={() => {
                setDataInicio(getPrimeiroDiaMesISO());
                setDataFim(getUltimoDiaMesISO());
                setPeriodoPreset('este_mes');
              }}
              className={`px-2.5 py-1 text-[11px] font-semibold rounded-lg transition-colors ${
                periodoPreset === 'este_mes'
                  ? 'bg-emerald-600 text-white shadow-xs'
                  : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
              }`}
            >
              Este Mês (Padrão)
            </button>
            <button
              type="button"
              onClick={() => {
                setDataInicio('');
                setDataFim('');
                setPeriodoPreset('todos');
              }}
              className={`px-2.5 py-1 text-[11px] font-semibold rounded-lg transition-colors ${
                periodoPreset === 'todos'
                  ? 'bg-gray-900 text-white shadow-xs'
                  : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
              }`}
            >
              Todo o Histórico
            </button>
            <button
              type="button"
              onClick={() => {
                setDataInicio(getHojeISO());
                setDataFim(getDeslocamentoDiasISO(30));
                setPeriodoPreset('proximos_30');
              }}
              className={`px-2.5 py-1 text-[11px] font-semibold rounded-lg transition-colors ${
                periodoPreset === 'proximos_30'
                  ? 'bg-emerald-600 text-white shadow-xs'
                  : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
              }`}
            >
              Próximos 30 dias
            </button>
            <button
              type="button"
              onClick={() => {
                setDataInicio(getHojeISO());
                setDataFim(getDeslocamentoDiasISO(7));
                setPeriodoPreset('proximos_7');
              }}
              className={`px-2.5 py-1 text-[11px] font-semibold rounded-lg transition-colors ${
                periodoPreset === 'proximos_7'
                  ? 'bg-emerald-600 text-white shadow-xs'
                  : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
              }`}
            >
              Próximos 7 dias
            </button>
            <button
              type="button"
              onClick={() => {
                setDataInicio(getHojeISO());
                setDataFim(getHojeISO());
                setPeriodoPreset('hoje');
              }}
              className={`px-2.5 py-1 text-[11px] font-semibold rounded-lg transition-colors ${
                periodoPreset === 'hoje'
                  ? 'bg-emerald-600 text-white shadow-xs'
                  : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
              }`}
            >
              Hoje
            </button>
          </div>
        </div>

        {/* Busca e Filtros de Categoria */}
        <div className="flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3.5 top-3 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar por colaborador, CPF, clínica, exame ou código do agendamento..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 text-xs border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
            />
          </div>

          <div className="flex items-center gap-1.5 overflow-x-auto pb-1 md:pb-0">
            <button
              onClick={() => setStatusFilter('todos')}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors ${
                statusFilter === 'todos'
                  ? 'bg-gray-900 text-white shadow-xs'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Todos ({totalNoPeriodo})
            </button>
            <button
              onClick={() => setStatusFilter('futuros')}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors ${
                statusFilter === 'futuros'
                  ? 'bg-emerald-600 text-white shadow-xs'
                  : 'bg-emerald-50 text-emerald-800 hover:bg-emerald-100'
              }`}
            >
              Futuros & Pendentes ({totalFuturos})
            </button>
            <button
              onClick={() => setStatusFilter('autoagendamento')}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors ${
                statusFilter === 'autoagendamento'
                  ? 'bg-blue-600 text-white shadow-xs'
                  : 'bg-blue-50 text-blue-700 hover:bg-blue-100'
              }`}
            >
              Autoagendamentos ({totalAuto})
            </button>
            <button
              onClick={() => setStatusFilter('rh')}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors ${
                statusFilter === 'rh'
                  ? 'bg-purple-600 text-white shadow-xs'
                  : 'bg-purple-50 text-purple-700 hover:bg-purple-100'
              }`}
            >
              Agendados RH ({totalRH})
            </button>
            <button
              onClick={() => setStatusFilter('candidatos')}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors ${
                statusFilter === 'candidatos'
                  ? 'bg-amber-600 text-white shadow-xs'
                  : 'bg-amber-50 text-amber-700 hover:bg-amber-100'
              }`}
            >
              Candidatos ({totalCandidatos})
            </button>
            <button
              onClick={() => setStatusFilter('expirados')}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors ${
                statusFilter === 'expirados'
                  ? 'bg-rose-600 text-white shadow-xs'
                  : 'bg-rose-50 text-rose-700 hover:bg-rose-100'
              }`}
            >
              Expirados ({totalExpirados})
            </button>
            <button
              onClick={() => setStatusFilter('concluidos')}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors ${
                statusFilter === 'concluidos'
                  ? 'bg-teal-600 text-white shadow-xs'
                  : 'bg-teal-50 text-teal-700 hover:bg-teal-100'
              }`}
            >
              Concluídos ({totalConcluidos})
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="py-16 text-center text-gray-500 text-xs">
            <RefreshCw className="w-6 h-6 animate-spin mx-auto text-emerald-600 mb-2" />
            Carregando agendamentos...
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center text-gray-500 text-xs">
            <Calendar className="w-10 h-10 mx-auto text-gray-300 mb-3" />
            <p className="font-bold text-gray-700 text-sm">Nenhum agendamento localizado</p>
            <p className="text-gray-400 mt-1 max-w-sm mx-auto">
              {statusFilter === 'futuros'
                ? 'Não existem exames futuros agendados ou pendentes no momento.'
                : 'Não encontramos nenhum agendamento para o filtro ou termo pesquisado.'}
            </p>
            {statusFilter !== 'todos' ? (
              <button
                onClick={() => setStatusFilter('todos')}
                className="mt-4 inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-semibold rounded-lg transition-colors"
              >
                Ver todo o histórico
              </button>
            ) : (
              <button
                onClick={onNovoAgendamento}
                className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded-xl shadow-sm"
              >
                <Plus className="w-3.5 h-3.5" />
                Agendar Primeiro Exame
              </button>
            )}
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-left text-xs text-gray-600">
                <thead className="bg-gray-50/90 text-[11px] uppercase font-bold text-gray-500 border-b border-gray-200">
                  <tr>
                    <th className="py-3 px-3 whitespace-nowrap">Código / Data</th>
                    <th className="py-3 px-3">Colaborador</th>
                    <th className="py-3 px-3">Exame</th>
                    <th className="py-3 px-3">Clínica & Agendamento</th>
                    <th className="py-3 px-3 whitespace-nowrap">Status</th>
                    <th className="py-3 px-4 text-right whitespace-nowrap sticky right-0 bg-gray-50/95 backdrop-blur-xs z-20 shadow-[-8px_0_12px_-4px_rgba(0,0,0,0.06)] border-l border-gray-200">
                      Ações & Gestão
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {paginatedArvoreCustomer.map((item, idx) => {
                    const agendamentoId = item.id || (item.agendamento_ids && item.agendamento_ids[0]);
                    const filhosTree = item.sub_agendamentos_detalhados || [];
                    const temFilhos = filhosTree.length > 0;
                    const isExpandido = subAgendamentosExpandidosCustomer[agendamentoId] !== false;
                    const isActive = item.status_badge !== 'green' && item.status_badge !== 'red';
                    const isConcluidoOuAuditoria =
                      Boolean(item.has_aso) ||
                      item.status === 'Realizado' ||
                      item.status === 'Concluído' ||
                      item.status === 'Em Auditoria' ||
                      item.status === 'Duplo Check' ||
                      item.status_operacional === 'EM_AUDITORIA' ||
                      item.status_operacional === 'LAUDO_EM_AUDITORIA' ||
                      item.status_operacional === 'ON_HOLD' ||
                      item.status_operacional === 'AGUARDANDO_RETORNO';
                    const hojeSP = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo' });
                    const itemDataStr = item.data_agendamento
                      ? (item.data_agendamento.includes('T') ? item.data_agendamento.split('T')[0] : item.data_agendamento)
                      : null;
                    const isPassado = itemDataStr ? itemDataStr < hojeSP : false;

                    const isAutoagendamentoPendente =
                      item.escolha_clinica_tipo === 'colaborador' &&
                      (!item.clinica_id ||
                        !item.data_agendamento ||
                        !item.clinica_nome ||
                        item.clinica_nome === '-' ||
                        item.clinica_nome.toLowerCase().includes('definir') ||
                        item.clinica_nome.toLowerCase().includes('pendente'));

                    const temClinica = Boolean(
                      item.clinica_id &&
                      item.data_agendamento &&
                      item.clinica_nome &&
                      item.clinica_nome !== '-' &&
                      !item.clinica_nome.toLowerCase().includes('definir') &&
                      !isAutoagendamentoPendente
                    );

                    const isPendenteColaborador = isAutoagendamentoPendente;

                    const isCancelavel =
                      !isConcluidoOuAuditoria &&
                      item.status !== 'Cancelado' &&
                      item.status_badge !== 'red' &&
                      item.status !== 'Aguardando Resultado' &&
                      (!isPassado || isPendenteColaborador || !temClinica);
                    const isPresencaConfirmadaClinica = Boolean(item.confirmou_presenca || item.status === 'Compareceu');
                    const isReagendavel =
                      !item.has_aso &&
                      item.status !== 'Realizado' &&
                      item.status !== 'Concluído' &&
                      item.status !== 'Cancelado' &&
                      item.status_badge !== 'red' &&
                      !isPresencaConfirmadaClinica;

                    return (
                      <React.Fragment key={agendamentoId || idx}>
                        <tr className={`group hover:bg-gray-50/80 transition-colors ${temFilhos ? 'bg-slate-50/40' : ''}`}>
                          {/* 1. Código / Data */}
                          <td className="py-3 px-3 whitespace-nowrap">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <span className="font-mono font-bold text-gray-900 text-xs bg-gray-100 px-2 py-0.5 rounded-md">
                                {item.codigo_pedido}
                              </span>
                              {item.is_sub_agendamento && (
                                <span
                                  className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-900 border border-amber-300 shrink-0"
                                  title={item.motivo_reagendamento ? `Motivo: ${item.motivo_reagendamento}` : `Sub-agendamento do mestre #${item.agendamento_pai_id}`}
                                >
                                  ↳ Sub #{item.agendamento_pai_id}
                                </span>
                              )}
                              {temFilhos && (
                                <button
                                  type="button"
                                  onClick={() => toggleSubAgendamentosCustomer(agendamentoId)}
                                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-purple-100 hover:bg-purple-200 text-purple-900 border border-purple-300 transition cursor-pointer shadow-2xs shrink-0"
                                  title="Clique para expandir/recolher sub-agendamentos na árvore"
                                >
                                  <ChevronDown
                                    className={`w-3 h-3 transition-transform duration-200 ${
                                      isExpandido ? 'rotate-0' : '-rotate-90'
                                    }`}
                                  />
                                  <span>Mestre ({filhosTree.length})</span>
                                </button>
                              )}
                            </div>
                            <p className="text-[10px] text-gray-400 mt-1">
                              Emissão: {new Date(item.created_at).toLocaleDateString('pt-BR')}
                            </p>
                          </td>

                          {/* 2. Colaborador (com Modalidade embutida) */}
                          <td className="py-3 px-3 min-w-[170px] max-w-[210px]">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <span className="font-bold text-gray-900 text-xs truncate max-w-[140px]" title={item.colaborador_nome}>
                                {item.colaborador_nome}
                              </span>
                              {item.is_candidato && (
                                <span className="bg-amber-100 text-amber-800 text-[9px] font-bold px-1.5 py-0.2 rounded-full border border-amber-200 shrink-0">
                                  Candidato
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-1.5 mt-0.5 text-[11px] text-gray-500 flex-wrap">
                              <span>CPF: {formatCPF(item.colaborador_cpf)}</span>
                              <span className="text-gray-300">•</span>
                              {item.escolha_clinica_tipo === 'colaborador' ? (
                                <span className="inline-flex items-center gap-0.5 px-1.5 py-0.2 rounded text-[9px] font-bold bg-blue-50 text-blue-700 border border-blue-200 shrink-0">
                                  <ExternalLink className="w-2.5 h-2.5" />
                                  Colaborador
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-1.5 py-0.2 rounded text-[9px] font-semibold bg-purple-50 text-purple-700 border border-purple-200 shrink-0">
                                  RH Direto
                                </span>
                              )}
                            </div>
                          </td>

                          {/* 3. Exame */}
                          <td className="py-3 px-3 min-w-[160px] max-w-[200px]">
                            <div className="space-y-1">
                              <span
                                className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-bold bg-emerald-50 text-emerald-800 border border-emerald-200 truncate max-w-full"
                                title={item.exame_nome}
                              >
                                {item.exame_nome}
                              </span>
                              {item.exames_clinicos && item.exames_clinicos.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-0.5">
                                  {item.exames_clinicos.slice(0, 2).map((exNome: string, exIdx: number) => (
                                    <span
                                      key={exIdx}
                                      className="inline-flex items-center text-[10px] font-medium bg-gray-100 text-gray-700 px-1.5 py-0.5 rounded border border-gray-200 truncate max-w-[130px]"
                                      title={exNome}
                                    >
                                      {formatarNomeExameAmigavel(exNome)}
                                    </span>
                                  ))}
                                  {item.exames_clinicos.length > 2 && (
                                    <span className="text-[10px] text-gray-400 font-semibold self-center">
                                      +{item.exames_clinicos.length - 2}
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          </td>

                          {/* 4. Clínica & Agendamento */}
                          <td className="py-3 px-3 min-w-[170px] max-w-[210px]">
                            {isPendenteColaborador || !temClinica ? (
                              <div>
                                {isExpirado(item) ? (
                                  <>
                                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-rose-50 text-rose-800 border border-rose-200">
                                      <Clock className="w-2.5 h-2.5 text-rose-600 shrink-0" />
                                      Expirado
                                    </span>
                                    <p className="text-[11px] text-rose-600 font-semibold mt-0.5 truncate" title={`Expirou em ${formatDateBR(getPrazoItem(item))}`}>
                                      Expirou em {formatDateBR(getPrazoItem(item))}
                                    </p>
                                  </>
                                ) : (
                                  <>
                                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-50 text-amber-800 border border-amber-200">
                                      <Clock className="w-2.5 h-2.5 text-amber-600 shrink-0" />
                                      Pendente Escolha
                                    </span>
                                    <p className="text-[11px] text-amber-800 font-medium mt-0.5 truncate" title={`Expira em ${formatDateBR(getPrazoItem(item))}`}>
                                      Expira em {formatDateBR(getPrazoItem(item))}
                                    </p>
                                  </>
                                )}
                              </div>
                            ) : (
                              <div>
                                <p className="font-semibold text-gray-900 text-xs truncate max-w-[170px]" title={item.clinica_nome}>
                                  {item.clinica_nome}
                                </p>
                                <p className="text-[11px] text-emerald-800 font-bold flex items-center gap-1 mt-0.5">
                                  <Calendar className="w-3 h-3 text-emerald-600 shrink-0" />
                                  <span>{formatDateBR(item.data_agendamento)}</span>

                                </p>
                                {item.clinica_localizacao && item.clinica_localizacao !== '-' && (
                                  <p className="text-[10px] text-gray-400 flex items-center gap-1 truncate mt-0.5" title={item.clinica_localizacao}>
                                    <MapPin className="w-2.5 h-2.5 shrink-0" />
                                    <span className="truncate">{item.clinica_localizacao}</span>
                                  </p>
                                )}
                              </div>
                            )}
                          </td>

                          {/* 5. Status */}
                          <td className="py-3 px-3 whitespace-nowrap">
                            <span
                              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold ${
                                item.status_badge === 'green'
                                  ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
                                  : isExpirado(item)
                                  ? 'bg-rose-50 text-rose-800 border border-rose-200'
                                  : item.status_badge === 'red'
                                  ? 'bg-red-50 text-red-800 border border-red-200'
                                  : isPendenteColaborador || item.status_badge === 'amber'
                                  ? 'bg-amber-50 text-amber-800 border border-amber-200'
                                  : 'bg-blue-50 text-blue-800 border border-blue-200'
                              }`}
                            >
                              {item.status_badge === 'green' && <CheckCircle2 className="w-3 h-3 text-emerald-600" />}
                              {isExpirado(item) && <AlertCircle className="w-3 h-3 text-rose-600" />}
                              {!isExpirado(item) && (isPendenteColaborador || item.status_badge === 'amber') && <Clock className="w-3 h-3 text-amber-600" />}
                              {!isExpirado(item) && item.status_badge === 'red' && <XCircle className="w-3 h-3 text-red-600" />}
                              {!isExpirado(item) && item.status_badge === 'blue' && !isPendenteColaborador && <CheckCircle2 className="w-3 h-3 text-blue-600" />}
                              {isExpirado(item) ? 'Link Expirado' : isPendenteColaborador ? 'Aguardando Colaborador' : item.status}
                            </span>
                          </td>

                          {/* 6. Ações & Gestão (STICKY RIGHT) */}
                          <td className="py-3 px-4 text-right whitespace-nowrap sticky right-0 bg-white/95 group-hover:bg-gray-50/95 backdrop-blur-xs z-10 shadow-[-8px_0_12px_-4px_rgba(0,0,0,0.06)] border-l border-gray-100">
                            <div className="flex items-center justify-end gap-1.5 flex-wrap">
                              {/* 1. SE AUTOAGENDAMENTO EXPIRADO: Apenas Reagendar e Cancelar */}
                              {isPendenteColaborador && isExpirado(item) && (
                                <>
                                  <button
                                    type="button"
                                    onClick={() => handleAbrirReagendarExpirado(item)}
                                    className="inline-flex items-center gap-1 text-[11px] text-indigo-700 hover:text-indigo-900 font-bold bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 px-2 py-1 rounded-lg transition-colors shadow-2xs"
                                    title="Reagendar autoagendamento expirado para nova data limite"
                                  >
                                    <Clock className="w-3 h-3 text-indigo-600" />
                                    <span>Reagendar</span>
                                  </button>

                                  <button
                                    type="button"
                                    onClick={() => setCancelandoItem(item)}
                                    className="inline-flex items-center gap-1 text-[11px] text-red-700 hover:text-red-900 font-semibold bg-red-50 hover:bg-red-100 border border-red-200 px-2 py-1 rounded-lg transition-colors"
                                    title="Cancelar agendamento expirado"
                                  >
                                    <Trash2 className="w-3 h-3" />
                                    <span>Cancelar</span>
                                  </button>
                                </>
                              )}

                              {/* 2. SE AUTOAGENDAMENTO VIGENTE (NÃO EXPIRADO): Copiar Link, Reenviar Lembrete e Cancelar */}
                              {isPendenteColaborador && !isExpirado(item) && item.token_escolha_clinica && isActive && !isPassado && (
                                <>
                                  <button
                                    type="button"
                                    onClick={() => handleCopyLink(item.token_escolha_clinica!)}
                                    className="inline-flex items-center gap-1 text-[11px] text-gray-700 hover:text-emerald-700 font-semibold bg-gray-50 hover:bg-emerald-50 border border-gray-200 px-2 py-1 rounded-lg transition-colors"
                                    title="Copiar link de autoagendamento"
                                  >
                                    {copiedToken === item.token_escolha_clinica ? (
                                      <>
                                        <Check className="w-3 h-3 text-emerald-600" />
                                        <span className="text-emerald-700 font-bold">Copiado</span>
                                      </>
                                    ) : (
                                      <>
                                        <Copy className="w-3 h-3 text-gray-500" />
                                        <span>Copiar Link</span>
                                      </>
                                    )}
                                  </button>

                                  {item.whatsapp_status && (
                                    <span
                                      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold border ${
                                        item.whatsapp_status === 'read'
                                          ? 'bg-blue-50 text-blue-700 border-blue-200'
                                          : item.whatsapp_status === 'delivered'
                                          ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                          : item.whatsapp_status === 'failed'
                                          ? 'bg-red-50 text-red-700 border-red-200'
                                          : 'bg-amber-50 text-amber-700 border-amber-200'
                                      }`}
                                      title={`WhatsApp: ${item.whatsapp_status_label || item.whatsapp_status}`}
                                    >
                                      <span className="w-1.5 h-1.5 rounded-full bg-current"></span>
                                      Zap {item.whatsapp_status_label || 'OK'}
                                    </span>
                                  )}

                                  <button
                                    type="button"
                                    onClick={() => handleReenviarLembrete(item.token_escolha_clinica!)}
                                    disabled={reenviandoToken === item.token_escolha_clinica}
                                    className="inline-flex items-center gap-1 text-[11px] text-blue-700 hover:text-blue-900 font-semibold bg-blue-50 hover:bg-blue-100 border border-blue-200 px-2 py-1 rounded-lg transition-colors"
                                    title="Reenviar link para o colaborador escolher clínica"
                                  >
                                    <Send className="w-3 h-3" />
                                    <span>{reenviandoToken === item.token_escolha_clinica ? 'Enviando...' : 'Reenviar Lembrete'}</span>
                                  </button>

                                  {isCancelavel && (
                                    <button
                                      type="button"
                                      onClick={() => setCancelandoItem(item)}
                                      className="inline-flex items-center gap-1 text-[11px] text-red-700 hover:text-red-900 font-semibold bg-red-50 hover:bg-red-100 border border-red-200 px-2 py-1 rounded-lg transition-colors"
                                      title="Cancelar agendamento"
                                    >
                                      <Trash2 className="w-3 h-3" />
                                      <span>Cancelar</span>
                                    </button>
                                  )}
                                </>
                              )}

                              {/* 3. SE CLÍNICA JÁ ESCOLHIDA OU RH DIRETO */}
                              {!isPendenteColaborador && temClinica && (
                                <>
                                  <button
                                    type="button"
                                    onClick={() => handleDownload('guia', item)}
                                    disabled={downloadingDoc[`guia_${item.id || item.pedido_id}_${item.colaborador_id}`]}
                                    className="inline-flex items-center gap-1 text-[11px] text-emerald-700 hover:text-emerald-900 font-semibold bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 px-2 py-1 rounded-lg transition-colors disabled:opacity-50"
                                    title="Baixar Guia de Encaminhamento"
                                  >
                                    <FileText className="w-3 h-3" />
                                    <span>{downloadingDoc[`guia_${item.id || item.pedido_id}_${item.colaborador_id}`] ? '...' : 'Guia'}</span>
                                  </button>

                                  <button
                                    type="button"
                                    onClick={() => handleDownload('kit-exame', item)}
                                    disabled={downloadingDoc[`kit-exame_${item.id || item.pedido_id}_${item.colaborador_id}`]}
                                    className="inline-flex items-center gap-1 text-[11px] text-purple-700 hover:text-purple-900 font-semibold bg-purple-50 hover:bg-purple-100 border border-purple-200 px-2 py-1 rounded-lg transition-colors disabled:opacity-50"
                                    title="Abrir Kit Exame Completo"
                                  >
                                    <Package className="w-3 h-3" />
                                    <span>{downloadingDoc[`kit-exame_${item.id || item.pedido_id}_${item.colaborador_id}`] ? '...' : 'Kit'}</span>
                                  </button>

                                  {item.has_aso && (
                                    <button
                                      type="button"
                                      onClick={() => handleDownloadAso(item.colaborador_id, item.colaborador_nome, item.aso_resultado_id, item.id)}
                                      disabled={downloadingAsoId === item.colaborador_id}
                                      className="inline-flex items-center gap-1 text-[11px] text-teal-700 hover:text-teal-900 font-bold bg-teal-50 hover:bg-teal-100 border border-teal-300 px-2 py-1 rounded-lg transition-colors shadow-2xs disabled:opacity-50 cursor-pointer"
                                      title="Baixar ASO (Atestado de Saúde Ocupacional)"
                                    >
                                      {downloadingAsoId === item.colaborador_id ? (
                                        <>
                                          <RefreshCw className="w-3 h-3 animate-spin" />
                                          <span>Baixando...</span>
                                        </>
                                      ) : (
                                        <>
                                          <FileDown className="w-3 h-3 text-teal-600" />
                                          <span>ASO</span>
                                        </>
                                      )}
                                    </button>
                                  )}

                                  {isReagendavel && (
                                    <button
                                      type="button"
                                      onClick={() => handleAbrirReagendar(item)}
                                      className="inline-flex items-center gap-1 text-[11px] text-indigo-700 hover:text-indigo-900 font-semibold bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 px-2 py-1 rounded-lg transition-colors"
                                      title="Reagendar exame"
                                    >
                                      <Edit2 className="w-3 h-3" />
                                      <span>Reagendar</span>
                                    </button>
                                  )}

                                  {isCancelavel && (
                                    <button
                                      type="button"
                                      onClick={() => setCancelandoItem(item)}
                                      className="inline-flex items-center gap-1 text-[11px] text-red-700 hover:text-red-900 font-semibold bg-red-50 hover:bg-red-100 border border-red-200 px-2 py-1 rounded-lg transition-colors"
                                      title="Cancelar agendamento"
                                    >
                                      <Trash2 className="w-3 h-3" />
                                      <span>Cancelar</span>
                                    </button>
                                  )}
                                </>
                              )}

                              {/* Histórico de Auditoria */}
                              <button
                                type="button"
                                onClick={() => handleAbrirHistoricoStatus(item)}
                                className="inline-flex items-center gap-1 text-[11px] text-slate-700 hover:text-slate-900 font-semibold bg-slate-50 hover:bg-slate-100 border border-slate-200 px-2 py-1 rounded-lg transition-colors"
                                title="Ver histórico de status do agendamento"
                              >
                                <History className="w-3 h-3 text-slate-500" />
                                <span>Histórico</span>
                              </button>

                              {/* Comprovante de Presença */}
                              {(item.confirmou_presenca || item.status === 'Presente' || item.status === 'Compareceu') && (
                                <button
                                  type="button"
                                  onClick={() => setSelectedComprovante(item)}
                                  className="inline-flex items-center gap-1 text-[11px] text-emerald-800 hover:text-emerald-950 font-semibold bg-emerald-50 hover:bg-emerald-100 border border-emerald-300 px-2 py-1 rounded-lg transition-colors cursor-pointer"
                                  title="Ver dados e foto de confirmação de presença"
                                >
                                  <ShieldCheck className="w-3 h-3 text-emerald-600" />
                                  <span>Comprovante</span>
                                </button>
                              )}

                              {/* Confirmação de Intenção */}
                              {!(item.confirmou_presenca || item.status === 'Presente' || item.status === 'Compareceu') && (item.confirmou_intencao || item.status === 'Confirmado') && (
                                <button
                                  type="button"
                                  onClick={() => setSelectedComprovante(item)}
                                  className="inline-flex items-center gap-1 text-[11px] text-blue-800 hover:text-blue-950 font-semibold bg-blue-50 hover:bg-blue-100 border border-blue-300 px-2 py-1 rounded-lg transition-colors cursor-pointer"
                                  title="Ver detalhes da confirmação de comparecimento"
                                >
                                  <CheckCircle2 className="w-3 h-3 text-blue-600" />
                                  <span>Ver Confirmação</span>
                                </button>
                              )}

                              {!isActive && !temClinica && !isExpirado(item) && (
                                <span className="text-[11px] text-gray-400 italic">Sem ações pendentes</span>
                              )}
                            </div>
                          </td>
                        </tr>

                        {/* SUB-AGENDAMENTOS NA ÁRVORE (CUSTOMER DESKTOP) */}
                        {temFilhos &&
                          isExpandido &&
                          filhosTree.map((sub) => {
                            const subId = sub.id || (sub.agendamento_ids && sub.agendamento_ids[0]);
                            const subTemClinica = Boolean(sub.clinica_id && sub.clinica_nome && sub.clinica_nome !== '-');
                            return (
                              <tr
                                key={`sub_cust_${subId}`}
                                className="bg-purple-50/20 hover:bg-purple-50/40 border-l-4 border-l-purple-500 border-b border-b-purple-100 transition-colors"
                              >
                                <td className="py-2.5 px-3 whitespace-nowrap pl-6">
                                  <div className="flex items-center gap-1.5">
                                    <span className="text-purple-600 font-black text-sm select-none">↳</span>
                                    <span className="font-mono font-bold text-purple-950 text-xs bg-purple-100/70 px-1.5 py-0.5 rounded border border-purple-200">
                                      #{subId}
                                    </span>
                                    <span className="inline-flex items-center px-1.5 py-0.2 rounded text-[9px] font-extrabold bg-purple-100 text-purple-800 border border-purple-200">
                                      SUB
                                    </span>
                                  </div>
                                  <p className="text-[10px] text-purple-700 font-medium pl-3.5 mt-0.5 truncate max-w-[170px]" title={sub.motivo_reagendamento || 'Recoleta / Reagendamento Pontual'}>
                                    {sub.motivo_reagendamento || 'Recoleta / Reagendamento'}
                                  </p>
                                </td>

                                <td className="py-2.5 px-3 min-w-[170px] max-w-[210px]">
                                  <span className="font-semibold text-gray-800 text-xs truncate block" title={sub.colaborador_nome}>
                                    {sub.colaborador_nome}
                                  </span>
                                  <div className="flex items-center gap-1.5 text-[10px] text-gray-500 mt-0.5">
                                    <span>CPF: {formatCPF(sub.colaborador_cpf)}</span>
                                    <span className="text-gray-300">•</span>
                                    <span className="inline-flex items-center px-1.5 py-0.2 rounded text-[9px] font-semibold bg-purple-100 text-purple-800 border border-purple-200">
                                      {sub.escolha_clinica_tipo === 'colaborador' ? 'Colab' : 'RH'}
                                    </span>
                                  </div>
                                </td>

                                <td className="py-2.5 px-3 min-w-[160px] max-w-[200px]">
                                  <div className="space-y-0.5">
                                    {sub.exames_clinicos && sub.exames_clinicos.length > 0 ? (
                                      sub.exames_clinicos.map((exNome: string, exIdx: number) => (
                                        <span
                                          key={exIdx}
                                          className="inline-flex items-center text-[10px] font-bold bg-purple-100 text-purple-800 px-1.5 py-0.5 rounded border border-purple-200 truncate max-w-full"
                                        >
                                          {formatarNomeExameAmigavel(exNome)}
                                        </span>
                                      ))
                                    ) : (
                                      <span className="text-[10px] text-gray-500 font-medium">{sub.exame_nome}</span>
                                    )}
                                  </div>
                                </td>

                                <td className="py-2.5 px-3 min-w-[170px] max-w-[210px]">
                                  {subTemClinica ? (
                                    <div>
                                      <p className="font-semibold text-gray-900 text-xs truncate max-w-[170px]" title={sub.clinica_nome}>
                                        {sub.clinica_nome}
                                      </p>
                                      <p className="text-[11px] text-purple-900 font-medium flex items-center gap-1 mt-0.5">
                                        <Calendar className="w-3 h-3 text-purple-600 shrink-0" />
                                        <span>{sub.data_agendamento ? formatDateBR(sub.data_agendamento) : 'A definir'}</span>
                                      </p>
                                    </div>
                                  ) : (
                                    <span className="text-[10px] text-amber-700 italic">A definir</span>
                                  )}
                                </td>

                                <td className="py-2.5 px-3 whitespace-nowrap">
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-purple-100 text-purple-800 border border-purple-200">
                                    <Clock className="w-2.5 h-2.5 text-purple-600" />
                                    <span>{sub.status || 'Agendado'}</span>
                                  </span>
                                </td>

                                <td className="py-2.5 px-4 text-right whitespace-nowrap sticky right-0 bg-purple-50/80 backdrop-blur-xs z-10 shadow-[-8px_0_12px_-4px_rgba(0,0,0,0.06)] border-l border-purple-200">
                                  <div className="flex items-center justify-end gap-1.5">
                                    {subTemClinica && sub.data_agendamento && (
                                      <button
                                        type="button"
                                        onClick={() => handleDownload('guia', sub)}
                                        className="inline-flex items-center gap-1 text-[10px] text-emerald-800 bg-emerald-50 hover:bg-emerald-100 border border-emerald-300 px-2 py-1 rounded-lg font-bold transition-colors cursor-pointer"
                                        title="Baixar Guia de Encaminhamento da recoleta"
                                      >
                                        <FileDown className="w-3 h-3 text-emerald-600" />
                                        <span>Guia Recoleta</span>
                                      </button>
                                    )}
                                    {sub.has_aso && (
                                      <button
                                        type="button"
                                        onClick={() => handleDownloadAso(sub.colaborador_id, sub.colaborador_nome, sub.aso_resultado_id, sub.id)}
                                        className="inline-flex items-center gap-1 text-[10px] text-indigo-800 bg-indigo-50 hover:bg-indigo-100 border border-indigo-300 px-2 py-1 rounded-lg font-bold transition-colors cursor-pointer"
                                      >
                                        <FileText className="w-3 h-3 text-indigo-600" />
                                        <span>ASO</span>
                                      </button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards (exibido apenas em telas menores que md) */}
            <div className="block md:hidden divide-y divide-gray-100 bg-white">
              {paginatedArvoreCustomer.map((item, idx) => {
                const agendamentoId = item.id || (item.agendamento_ids && item.agendamento_ids[0]);
                const filhosTree = item.sub_agendamentos_detalhados || [];
                const temFilhos = filhosTree.length > 0;
                const isExpandido = subAgendamentosExpandidosCustomer[agendamentoId] !== false;
                const isActive = item.status_badge !== 'green' && item.status_badge !== 'red';
                const isConcluidoOuAuditoria =
                  Boolean(item.has_aso) ||
                  item.status === 'Realizado' ||
                  item.status === 'Concluído' ||
                  item.status === 'Em Auditoria' ||
                  item.status === 'Duplo Check' ||
                  item.status_operacional === 'EM_AUDITORIA' ||
                  item.status_operacional === 'LAUDO_EM_AUDITORIA' ||
                  item.status_operacional === 'ON_HOLD' ||
                  item.status_operacional === 'AGUARDANDO_RETORNO';
                const hojeSP = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo' });
                const itemDataStr = item.data_agendamento
                  ? (item.data_agendamento.includes('T') ? item.data_agendamento.split('T')[0] : item.data_agendamento)
                  : null;
                const isPassado = itemDataStr ? itemDataStr < hojeSP : false;

                const isAutoagendamentoPendente =
                  item.escolha_clinica_tipo === 'colaborador' &&
                  (!item.clinica_id ||
                    !item.data_agendamento ||
                    !item.clinica_nome ||
                    item.clinica_nome === '-' ||
                    item.clinica_nome.toLowerCase().includes('definir') ||
                    item.clinica_nome.toLowerCase().includes('pendente'));

                const temClinica = Boolean(
                  item.clinica_id &&
                  item.data_agendamento &&
                  item.clinica_nome &&
                  item.clinica_nome !== '-' &&
                  !item.clinica_nome.toLowerCase().includes('definir') &&
                  !isAutoagendamentoPendente
                );

                const isPendenteColaborador = isAutoagendamentoPendente;

                const isCancelavel =
                  !isConcluidoOuAuditoria &&
                  item.status !== 'Cancelado' &&
                  item.status_badge !== 'red' &&
                  item.status !== 'Aguardando Resultado' &&
                  (!isPassado || isPendenteColaborador || !temClinica);
                const isPresencaConfirmadaClinica = Boolean(item.confirmou_presenca || item.status === 'Compareceu');
                const isReagendavel =
                  !item.has_aso &&
                  item.status !== 'Realizado' &&
                  item.status !== 'Concluído' &&
                  item.status !== 'Cancelado' &&
                  item.status_badge !== 'red' &&
                  !isPresencaConfirmadaClinica;

                return (
                  <div key={agendamentoId || idx} className="p-4 space-y-3 hover:bg-gray-50/50 transition-colors">
                    {/* Topo do Card: Código, Emissão e Status Badge */}
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-1.5">
                        <span className="font-mono font-bold text-gray-900 text-[11px] bg-gray-100 px-2 py-0.5 rounded-md border border-gray-200">
                          {item.codigo_pedido}
                        </span>
                        <span className="text-[10px] text-gray-400">
                          {new Date(item.created_at).toLocaleDateString('pt-BR')}
                        </span>
                      </div>

                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${
                          item.status_badge === 'green'
                            ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
                            : isExpirado(item)
                            ? 'bg-rose-50 text-rose-800 border border-rose-200'
                            : item.status_badge === 'red'
                            ? 'bg-red-50 text-red-800 border border-red-200'
                            : isPendenteColaborador || item.status_badge === 'amber'
                            ? 'bg-amber-50 text-amber-800 border border-amber-200'
                            : 'bg-blue-50 text-blue-800 border border-blue-200'
                        }`}
                      >
                        {item.status_badge === 'green' && <CheckCircle2 className="w-3 h-3 text-emerald-600" />}
                        {isExpirado(item) && <AlertCircle className="w-3 h-3 text-rose-600" />}
                        {!isExpirado(item) && (isPendenteColaborador || item.status_badge === 'amber') && <Clock className="w-3 h-3 text-amber-600" />}
                        {!isExpirado(item) && item.status_badge === 'red' && <XCircle className="w-3 h-3 text-red-600" />}
                        {!isExpirado(item) && item.status_badge === 'blue' && !isPendenteColaborador && <CheckCircle2 className="w-3 h-3 text-blue-600" />}
                        {isExpirado(item) ? 'Link Expirado' : isPendenteColaborador ? 'Aguardando Colaborador' : item.status}
                      </span>
                    </div>

                    {/* Dados do Colaborador & Modalidade */}
                    <div>
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="font-bold text-gray-900 text-sm">{item.colaborador_nome}</span>
                        {item.is_sub_agendamento && (
                          <span
                            className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-900 border border-amber-300"
                            title={item.motivo_reagendamento ? `Motivo: ${item.motivo_reagendamento}` : `Sub-agendamento do mestre #${item.agendamento_pai_id}`}
                          >
                            ↳ 🌿 Sub de #{item.agendamento_pai_id}
                          </span>
                        )}
                        {temFilhos && (
                          <button
                            type="button"
                            onClick={() => toggleSubAgendamentosCustomer(agendamentoId)}
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold bg-purple-100 hover:bg-purple-200 text-purple-900 border border-purple-300 transition cursor-pointer shadow-2xs"
                            title="Clique para expandir/recolher sub-agendamentos na árvore"
                          >
                            <ChevronDown
                              className={`w-3 h-3 transition-transform duration-200 ${
                                isExpandido ? 'rotate-0' : '-rotate-90'
                              }`}
                            />
                            <span>📦 Mestre ({filhosTree.length} sub)</span>
                          </button>
                        )}
                        {item.is_candidato && (
                          <span className="bg-amber-100 text-amber-800 text-[10px] font-bold px-1.5 py-0.5 rounded-full border border-amber-200 shrink-0">
                            Candidato
                          </span>
                        )}
                        {item.escolha_clinica_tipo === 'colaborador' ? (
                          <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold bg-blue-50 text-blue-700 border border-blue-200 shrink-0">
                            <ExternalLink className="w-2.5 h-2.5" />
                            Colaborador
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-purple-50 text-purple-700 border border-purple-200 shrink-0">
                            RH Direto
                          </span>
                        )}
                      </div>
                      <p className="text-[11px] text-gray-500 mt-0.5">CPF: {formatCPF(item.colaborador_cpf)}</p>
                    </div>

                    {/* Exames */}
                    <div className="space-y-1">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-bold bg-emerald-50 text-emerald-800 border border-emerald-200">
                          {item.exame_nome}
                        </span>
                        {item.exames_clinicos && item.exames_clinicos.map((exNome: string, exIdx: number) => (
                          <span
                            key={exIdx}
                            className="inline-flex items-center text-[10px] font-medium bg-gray-100 text-gray-700 px-1.5 py-0.5 rounded border border-gray-200"
                          >
                            {formatarNomeExameAmigavel(exNome)}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Clínica & Data do Exame */}
                    <div className="bg-gray-50 rounded-xl p-2.5 border border-gray-200/70 text-xs">
                      {isPendenteColaborador || !temClinica ? (
                        isExpirado(item) ? (
                          <div className="flex items-center justify-between gap-2 text-rose-800">
                            <div className="flex items-center gap-1.5">
                              <Clock className="w-3.5 h-3.5 text-rose-600 shrink-0" />
                              <span className="text-[11px] font-bold text-rose-700">Link Expirado</span>
                            </div>
                            <span className="text-[10px] text-rose-600 font-semibold">
                              Expirou em {formatDateBR(getPrazoItem(item))}
                            </span>
                          </div>
                        ) : (
                          <div className="flex items-center justify-between gap-2 text-amber-800">
                            <div className="flex items-center gap-1.5">
                              <Clock className="w-3.5 h-3.5 text-amber-600 shrink-0" />
                              <span className="text-[11px] font-medium text-amber-800">Pendente de escolha</span>
                            </div>
                            <span className="text-[10px] text-amber-700 font-medium">
                              Expira em {formatDateBR(getPrazoItem(item))}
                            </span>
                          </div>
                        )
                      ) : (
                        <div className="space-y-1">
                          <div className="flex items-center justify-between gap-2">
                            <span className="font-semibold text-gray-900 text-xs truncate">{item.clinica_nome}</span>
                            {item.data_agendamento && (
                              <span className="font-bold text-emerald-800 text-xs whitespace-nowrap flex items-center gap-1 shrink-0">
                                <Calendar className="w-3 h-3 text-emerald-600" />
                                {formatDateBR(item.data_agendamento)}
                              </span>
                            )}
                          </div>
                          {item.clinica_localizacao && item.clinica_localizacao !== '-' && (
                            <p className="text-[11px] text-gray-500 flex items-center gap-1 truncate">
                              <MapPin className="w-3 h-3 shrink-0 text-gray-400" />
                              <span className="truncate">{item.clinica_localizacao}</span>
                            </p>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Ações Touch-Friendly */}
                    <div className="pt-1 flex items-center gap-1.5 flex-wrap">
                      {/* 1. SE AUTOAGENDAMENTO EXPIRADO: Apenas Reagendar e Cancelar */}
                      {isPendenteColaborador && isExpirado(item) && (
                        <>
                          <button
                            type="button"
                            onClick={() => handleAbrirReagendarExpirado(item)}
                            className="flex-1 min-w-[130px] inline-flex items-center justify-center gap-1.5 text-xs text-indigo-700 hover:text-indigo-900 font-bold bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 py-2 px-2.5 rounded-xl transition-colors active:scale-95 shadow-2xs"
                            title="Reagendar autoagendamento expirado para nova data limite"
                          >
                            <Clock className="w-3.5 h-3.5 text-indigo-600" />
                            <span>Reagendar</span>
                          </button>

                          <button
                            type="button"
                            onClick={() => setCancelandoItem(item)}
                            className="flex-1 min-w-[110px] inline-flex items-center justify-center gap-1.5 text-xs text-red-700 hover:text-red-900 font-semibold bg-red-50 hover:bg-red-100 border border-red-200 py-2 px-2.5 rounded-xl transition-colors active:scale-95"
                            title="Cancelar agendamento expirado"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                            <span>Cancelar</span>
                          </button>
                        </>
                      )}

                      {/* 2. SE AUTOAGENDAMENTO VIGENTE (NÃO EXPIRADO): Copiar Link, Reenviar Lembrete e Cancelar */}
                      {isPendenteColaborador && !isExpirado(item) && item.token_escolha_clinica && isActive && !isPassado && (
                        <>
                          <button
                            type="button"
                            onClick={() => handleCopyLink(item.token_escolha_clinica!)}
                            className="flex-1 min-w-[120px] inline-flex items-center justify-center gap-1.5 text-xs text-gray-700 hover:text-emerald-700 font-semibold bg-gray-50 hover:bg-emerald-50 border border-gray-200 py-2 px-2.5 rounded-xl transition-colors active:scale-95"
                          >
                            {copiedToken === item.token_escolha_clinica ? (
                              <>
                                <Check className="w-3.5 h-3.5 text-emerald-600" />
                                <span className="text-emerald-700 font-bold">Copiado</span>
                              </>
                            ) : (
                              <>
                                <Copy className="w-3.5 h-3.5 text-gray-500" />
                                <span>Copiar Link</span>
                              </>
                            )}
                          </button>

                          {item.whatsapp_status && (
                            <span
                              className={`inline-flex items-center gap-1 px-2 py-1.5 rounded-xl text-[10px] font-bold border ${
                                item.whatsapp_status === 'read'
                                  ? 'bg-blue-50 text-blue-700 border-blue-200'
                                  : item.whatsapp_status === 'delivered'
                                  ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                  : item.whatsapp_status === 'failed'
                                  ? 'bg-red-50 text-red-700 border-red-200'
                                  : 'bg-amber-50 text-amber-700 border-amber-200'
                              }`}
                            >
                              <span className="w-1.5 h-1.5 rounded-full bg-current"></span>
                              Zap {item.whatsapp_status_label || 'OK'}
                            </span>
                          )}

                          <button
                            type="button"
                            onClick={() => handleReenviarLembrete(item.token_escolha_clinica!)}
                            disabled={reenviandoToken === item.token_escolha_clinica}
                            className="flex-1 min-w-[130px] inline-flex items-center justify-center gap-1.5 text-xs text-blue-700 hover:text-blue-900 font-semibold bg-blue-50 hover:bg-blue-100 border border-blue-200 py-2 px-2.5 rounded-xl transition-colors active:scale-95 disabled:opacity-50"
                          >
                            <Send className="w-3.5 h-3.5" />
                            <span>{reenviandoToken === item.token_escolha_clinica ? 'Enviando...' : 'Reenviar Lembrete'}</span>
                          </button>

                          {isCancelavel && (
                            <button
                              type="button"
                              onClick={() => setCancelandoItem(item)}
                              className="inline-flex items-center gap-1 text-xs text-red-700 hover:text-red-900 font-semibold bg-red-50 hover:bg-red-100 border border-red-200 py-2 px-2.5 rounded-xl transition-colors"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                              <span>Cancelar</span>
                            </button>
                          )}
                        </>
                      )}

                      {/* 3. SE CLÍNICA JÁ ESCOLHIDA OU RH DIRETO */}
                      {!isPendenteColaborador && temClinica && (
                        <>
                          <button
                            type="button"
                            onClick={() => handleDownload('guia', item)}
                            disabled={downloadingDoc[`guia_${item.id || item.pedido_id}_${item.colaborador_id}`]}
                            className="inline-flex items-center gap-1 text-xs text-emerald-700 hover:text-emerald-900 font-semibold bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 py-1.5 px-2.5 rounded-xl transition-colors disabled:opacity-50"
                          >
                            <FileText className="w-3.5 h-3.5" />
                            <span>{downloadingDoc[`guia_${item.id || item.pedido_id}_${item.colaborador_id}`] ? '...' : 'Guia'}</span>
                          </button>

                          <button
                            type="button"
                            onClick={() => handleDownload('kit-exame', item)}
                            disabled={downloadingDoc[`kit-exame_${item.id || item.pedido_id}_${item.colaborador_id}`]}
                            className="inline-flex items-center gap-1 text-xs text-purple-700 hover:text-purple-900 font-semibold bg-purple-50 hover:bg-purple-100 border border-purple-200 py-1.5 px-2.5 rounded-xl transition-colors disabled:opacity-50"
                          >
                            <Package className="w-3.5 h-3.5" />
                            <span>{downloadingDoc[`kit-exame_${item.id || item.pedido_id}_${item.colaborador_id}`] ? '...' : 'Kit'}</span>
                          </button>

                          {item.has_aso && (
                            <button
                              type="button"
                              onClick={() => handleDownloadAso(item.colaborador_id, item.colaborador_nome, item.aso_resultado_id, item.id)}
                              disabled={downloadingAsoId === item.colaborador_id}
                              className="inline-flex items-center gap-1 text-xs text-teal-700 hover:text-teal-900 font-bold bg-teal-50 hover:bg-teal-100 border border-teal-300 py-1.5 px-2.5 rounded-xl transition-colors disabled:opacity-50"
                            >
                              {downloadingAsoId === item.colaborador_id ? (
                                <>
                                  <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                                  <span>ASO...</span>
                                </>
                              ) : (
                                <>
                                  <FileDown className="w-3.5 h-3.5 text-teal-600" />
                                  <span>ASO</span>
                                </>
                              )}
                            </button>
                          )}

                          {isReagendavel && (
                            <button
                              type="button"
                              onClick={() => handleAbrirReagendar(item)}
                              className="inline-flex items-center gap-1 text-xs text-indigo-700 hover:text-indigo-900 font-semibold bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 py-1.5 px-2.5 rounded-xl transition-colors"
                            >
                              <Edit2 className="w-3.5 h-3.5" />
                              <span>Reagendar</span>
                            </button>
                          )}

                          {isCancelavel && (
                            <button
                              type="button"
                              onClick={() => setCancelandoItem(item)}
                              className="inline-flex items-center gap-1 text-xs text-red-700 hover:text-red-900 font-semibold bg-red-50 hover:bg-red-100 border border-red-200 py-1.5 px-2.5 rounded-xl transition-colors"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                              <span>Cancelar</span>
                            </button>
                          )}
                        </>
                      )}

                      {/* Histórico */}
                      <button
                        type="button"
                        onClick={() => handleAbrirHistoricoStatus(item)}
                        className="inline-flex items-center gap-1 text-xs text-slate-700 hover:text-slate-900 font-semibold bg-slate-50 hover:bg-slate-100 border border-slate-200 py-1.5 px-2.5 rounded-xl transition-colors"
                      >
                        <History className="w-3.5 h-3.5 text-slate-500" />
                        <span>Histórico</span>
                      </button>

                      {/* Comprovante Presença */}
                      {(item.confirmou_presenca || item.status === 'Presente' || item.status === 'Compareceu') && (
                        <button
                          type="button"
                          onClick={() => setSelectedComprovante(item)}
                          className="inline-flex items-center gap-1 text-xs text-emerald-800 hover:text-emerald-950 font-semibold bg-emerald-50 hover:bg-emerald-100 border border-emerald-300 py-1.5 px-2.5 rounded-xl transition-colors"
                          title="Ver dados e foto de confirmação de presença"
                        >
                          <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
                          <span>Comprovante</span>
                        </button>
                      )}

                      {/* Ver Confirmação Intenção */}
                      {!(item.confirmou_presenca || item.status === 'Presente' || item.status === 'Compareceu') && (item.confirmou_intencao || item.status === 'Confirmado') && (
                        <button
                          type="button"
                          onClick={() => setSelectedComprovante(item)}
                          className="inline-flex items-center gap-1 text-xs text-blue-800 hover:text-blue-950 font-semibold bg-blue-50 hover:bg-blue-100 border border-blue-300 py-1.5 px-2.5 rounded-xl transition-colors"
                        >
                          <CheckCircle2 className="w-3.5 h-3.5 text-blue-600" />
                          <span>Ver Confirmação</span>
                        </button>
                      )}
                    </div>

                    {/* SUB-AGENDAMENTOS NA ÁRVORE (CUSTOMER MOBILE) */}
                    {temFilhos && isExpandido && (
                      <div className="mt-3 pl-3 border-l-2 border-purple-300 space-y-2">
                        {filhosTree.map((sub) => {
                          const subId = sub.id || (sub.agendamento_ids && sub.agendamento_ids[0]);
                          const subTemClinica = Boolean(sub.clinica_id && sub.clinica_nome && sub.clinica_nome !== '-');
                          return (
                            <div
                              key={`sub_card_${subId}`}
                              className="bg-purple-50/40 rounded-xl p-3 border border-purple-200 space-y-2 text-xs"
                            >
                              <div className="flex items-center justify-between gap-1.5">
                                <div className="flex items-center gap-1">
                                  <span className="text-purple-600 font-bold select-none">↳</span>
                                  <span className="font-mono font-bold text-purple-950 text-[11px] bg-purple-100 px-1.5 py-0.5 rounded border border-purple-200">
                                    #{subId}
                                  </span>
                                  <span className="inline-flex items-center px-1.5 py-0.2 rounded text-[9px] font-extrabold bg-purple-200 text-purple-900">
                                    SUB-AGENDAMENTO
                                  </span>
                                </div>
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-purple-100 text-purple-800 border border-purple-200">
                                  <Clock className="w-2.5 h-2.5 text-purple-600" />
                                  <span>{sub.status || 'Agendado'}</span>
                                </span>
                              </div>

                              <p className="text-[10px] text-purple-800 font-medium">
                                {sub.motivo_reagendamento || 'Recoleta / Reagendamento'}
                              </p>

                              <div className="text-[11px] space-y-0.5">
                                <p className="font-semibold text-gray-900">{sub.clinica_nome || 'Clínica a definir'}</p>
                                {sub.data_agendamento && (
                                  <p className="text-emerald-800 font-bold flex items-center gap-1">
                                    <Calendar className="w-3 h-3 text-emerald-600" />
                                    {formatDateBR(sub.data_agendamento)}
                                  </p>
                                )}
                              </div>

                              <div className="pt-1 flex items-center gap-1.5">
                                {subTemClinica && sub.data_agendamento && (
                                  <button
                                    type="button"
                                    onClick={() => handleDownload('guia', sub)}
                                    className="flex-1 inline-flex items-center justify-center gap-1 text-[11px] text-emerald-800 bg-emerald-50 hover:bg-emerald-100 border border-emerald-300 py-1.5 px-2 rounded-lg font-bold cursor-pointer"
                                  >
                                    <FileDown className="w-3 h-3 text-emerald-600" />
                                    <span>Guia Recoleta</span>
                                  </button>
                                )}
                                {sub.has_aso && (
                                  <button
                                    type="button"
                                    onClick={() => handleDownloadAso(sub.colaborador_id, sub.colaborador_nome, sub.aso_resultado_id, sub.id)}
                                    className="flex-1 inline-flex items-center justify-center gap-1 text-[11px] text-indigo-800 bg-indigo-50 hover:bg-indigo-100 border border-indigo-300 py-1.5 px-2 rounded-lg font-bold cursor-pointer"
                                  >
                                    <FileText className="w-3 h-3 text-indigo-600" />
                                    <span>ASO</span>
                                  </button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Barra de Paginação Elegante */}
            {totalMestres > 0 && (
              <div className="bg-gray-50/70 border-t border-gray-200/80 px-4 py-3 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-gray-600 rounded-b-2xl">
                <div className="flex items-center gap-2 flex-wrap justify-center sm:justify-start">
                  <span>Exibindo</span>
                  <span className="font-semibold text-gray-900">
                    {Math.min((currentPage - 1) * pageSize + 1, totalMestres)} - {Math.min(currentPage * pageSize, totalMestres)}
                  </span>
                  <span>de</span>
                  <span className="font-semibold text-gray-900">{totalMestres}</span>
                  <span>agendamentos</span>

                  <div className="ml-2 pl-2 border-l border-gray-300 flex items-center gap-1.5">
                    <span className="text-gray-500">Por página:</span>
                    <select
                      value={pageSize}
                      onChange={(e) => {
                        setPageSize(Number(e.target.value));
                        setCurrentPage(1);
                      }}
                      className="border border-gray-300 rounded-md px-2 py-0.5 text-xs bg-white text-gray-800 font-medium focus:outline-none focus:ring-1 focus:ring-emerald-500 shadow-2xs"
                    >
                      <option value={15}>15</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                  </div>
                </div>

                {/* Controles de Navegação */}
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                    className="p-1.5 rounded-lg border border-gray-300 bg-white hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors text-gray-700 shadow-2xs"
                    title="Primeira página"
                  >
                    <ChevronsLeft className="w-3.5 h-3.5" />
                  </button>

                  <button
                    type="button"
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border border-gray-300 bg-white hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors text-gray-700 font-medium shadow-2xs"
                  >
                    <ChevronLeft className="w-3.5 h-3.5" />
                    <span>Anterior</span>
                  </button>

                  <span className="px-3 py-1 font-bold text-emerald-800 bg-emerald-50 border border-emerald-300 rounded-lg text-xs shadow-2xs">
                    {currentPage} / {totalPages}
                  </span>

                  <button
                    type="button"
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border border-gray-300 bg-white hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors text-gray-700 font-medium shadow-2xs"
                  >
                    <span>Próximo</span>
                    <ChevronRight className="w-3.5 h-3.5" />
                  </button>

                  <button
                    type="button"
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                    className="p-1.5 rounded-lg border border-gray-300 bg-white hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors text-gray-700 shadow-2xs"
                    title="Última página"
                  >
                    <ChevronsRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* ========================================================= */}
      {/* MODAL: CANCELAR AGENDAMENTO (RH OU AUTOAGENDAMENTO)       */}
      {/* ========================================================= */}
      {cancelandoItem && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl space-y-4 animate-in fade-in zoom-in duration-150">
            <div className="flex items-center justify-between pb-3 border-b border-gray-100">
              <div className="flex items-center gap-2 text-red-600">
                <AlertTriangle className="w-5 h-5" />
                <h3 className="text-base font-bold text-gray-900">Cancelar Agendamento</h3>
              </div>
              <button
                onClick={() => setCancelandoItem(null)}
                className="text-gray-400 hover:text-gray-600 p-1 rounded-lg"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="text-xs text-gray-600 space-y-2">
              <p>
                Tem certeza que deseja cancelar o agendamento de{' '}
                <strong className="text-gray-900">{cancelandoItem.colaborador_nome}</strong>?
              </p>
              <div className="bg-gray-50 p-3 rounded-xl border border-gray-200 space-y-1 text-[11px]">
                <p><strong>Exame:</strong> {cancelandoItem.exame_nome}</p>
                <p><strong>Clínica:</strong> {cancelandoItem.clinica_nome}</p>
                <p><strong>Código do Pedido:</strong> {cancelandoItem.codigo_pedido}</p>
              </div>
              <p className="text-gray-500">
                A clínica credenciada será notificada do cancelamento e a guia deixará de ser válida para atendimento.
              </p>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Motivo do cancelamento (opcional):
              </label>
              <textarea
                value={motivoCancelamento}
                onChange={(e) => setMotivoCancelamento(e.target.value)}
                placeholder="Ex.: Desistência do candidato, alteração de quadro, reagendamento com outra clínica..."
                className="w-full text-xs border border-gray-300 rounded-xl p-2.5 focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500"
                rows={2}
              />
            </div>

            <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-100">
              <button
                onClick={() => setCancelandoItem(null)}
                disabled={actionLoading}
                className="px-4 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-100 rounded-xl transition-colors"
              >
                Voltar
              </button>
              <button
                onClick={handleConfirmCancelar}
                disabled={actionLoading}
                className="px-4 py-2 text-xs font-bold text-white bg-red-600 hover:bg-red-700 rounded-xl shadow-sm transition-colors disabled:opacity-50"
              >
                {actionLoading ? 'Cancelando...' : 'Confirmar Cancelamento'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================= */}
      {/* MODAL: REAGENDAR DATA DO EXAME (RH DIRETO)               */}
      {/* ========================================================= */}
      {reagendandoItem && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl space-y-4 animate-in fade-in zoom-in duration-150 max-h-[92vh] overflow-y-auto">
            <div className="flex items-center justify-between pb-3 border-b border-gray-100">
              <div className="flex items-center gap-2 text-indigo-600">
                <Calendar className="w-5 h-5" />
                <h3 className="text-base font-bold text-gray-900">
                  {tipoReagendamento === 'parcial' ? 'Sub-agendamento / Reagendar Exames' : 'Reagendar Exame'}
                </h3>
              </div>
              <button
                onClick={() => setReagendandoItem(null)}
                className="text-gray-400 hover:text-gray-600 p-1 rounded-lg cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="text-xs text-gray-600 space-y-1">
              <p>
                Selecione a modalidade, clínica credenciada, exames e nova data para{' '}
                <strong className="text-gray-900">{reagendandoItem.colaborador_nome}</strong>:
              </p>
            </div>

            {/* Modalidade de Reagendamento: Total vs Parcial */}
            {itensReagendamento.length > 1 && (
              <div className="p-3 bg-gray-50 border border-gray-200 rounded-xl space-y-2">
                <span className="block font-semibold text-gray-700 text-xs">Modalidade de Reagendamento</span>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setTipoReagendamento('total');
                      setItensSelecionadosReagendar(itensReagendamento.map((i) => i.id));
                    }}
                    className={`p-2.5 rounded-lg border text-left transition-all cursor-pointer ${
                      tipoReagendamento === 'total'
                        ? 'bg-blue-50 border-blue-500 ring-1 ring-blue-500 text-blue-900 font-bold'
                        : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <div className="flex items-center gap-1.5">
                      <span className="text-sm">🔄</span>
                      <span className="text-xs">Atendimento Completo</span>
                    </div>
                    <p className="text-[11px] font-normal text-gray-500 mt-1">
                      Reagenda toda a consulta clínica e exames para nova data.
                    </p>
                  </button>

                  <button
                    type="button"
                    onClick={() => setTipoReagendamento('parcial')}
                    className={`p-2.5 rounded-lg border text-left transition-all cursor-pointer ${
                      tipoReagendamento === 'parcial'
                        ? 'bg-purple-50 border-purple-500 ring-1 ring-purple-500 text-purple-900 font-bold'
                        : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <div className="flex items-center gap-1.5">
                      <span className="text-sm">🌿</span>
                      <span className="text-xs">Exames Específicos (Parcial)</span>
                    </div>
                    <p className="text-[11px] font-normal text-gray-500 mt-1">
                      Cria reagendamento de recoletas ou exames pontuais.
                    </p>
                  </button>
                </div>
              </div>
            )}

            {/* Clínica Credenciada (Original por padrão + Trilha de Troca Estado -> Cidade -> Bairro) */}
            <div className="space-y-2">
              <label className="block text-xs font-semibold text-gray-700">
                Clínica Credenciada <span className="text-red-500">*</span>
              </label>

              {!alterandoClinica ? (
                <div className="p-3.5 bg-slate-50 border border-slate-200 rounded-xl space-y-2">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                          Clínica Selecionada
                        </span>
                        {reagendandoItem?.clinica_id && Number(novaClinicaReagendamento) === Number(reagendandoItem.clinica_id) ? (
                          <span className="text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded-full flex items-center gap-1">
                            ✓ Clínica Original
                          </span>
                        ) : (
                          <span className="text-[10px] font-semibold bg-blue-50 text-blue-700 border border-blue-200 px-2 py-0.5 rounded-full flex items-center gap-1">
                            Nova Clínica Selecionada
                          </span>
                        )}
                      </div>
                      <p className="text-sm font-bold text-gray-900 truncate">
                        {clinicaSelecionadaInfo?.nome || reagendandoItem?.clinica_nome || 'Clínica não informada'}
                      </p>
                      <p className="text-xs text-gray-600 mt-0.5 flex items-center gap-1">
                        <MapPin className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                        <span className="truncate">
                          {clinicaSelecionadaInfo?.localizacao || reagendandoItem?.clinica_localizacao || 'Endereço cadastrado'}
                        </span>
                      </p>
                    </div>
                    <div className="shrink-0 flex flex-col items-end gap-1.5">
                      <button
                        type="button"
                        onClick={() => {
                          setAlterandoClinica(true);
                          carregarEstados();
                        }}
                        className="px-3 py-1.5 bg-white border border-gray-300 hover:border-primary-500 text-primary-700 hover:text-primary-800 text-xs font-semibold rounded-lg shadow-sm hover:shadow transition-all flex items-center gap-1.5"
                      >
                        <Building2 className="w-3.5 h-3.5" />
                        Mudar clínica
                      </button>
                      {reagendandoItem?.clinica_id && Number(novaClinicaReagendamento) !== Number(reagendandoItem.clinica_id) && (
                        <button
                          type="button"
                          onClick={restaurarClinicaOriginal}
                          className="text-[11px] text-gray-500 hover:text-gray-800 underline"
                        >
                          Voltar para original
                        </button>
                      )}
                    </div>
                  </div>

                  {reagendandoItem &&
                    novaClinicaReagendamento &&
                    reagendandoItem.clinica_id &&
                    Number(novaClinicaReagendamento) !== Number(reagendandoItem.clinica_id) && (
                      <div className="p-2 bg-amber-50 border border-amber-200 rounded-lg text-[11px] text-amber-800 flex items-start gap-1.5 mt-2">
                        <AlertTriangle className="w-3.5 h-3.5 text-amber-600 shrink-0 mt-0.5" />
                        <span>
                          {tipoReagendamento === 'parcial'
                            ? 'O sub-agendamento será direcionado para esta nova clínica credenciada.'
                            : 'O agendamento anterior será transferido para esta nova clínica com guia atualizada.'}
                        </span>
                      </div>
                    )}
                </div>
              ) : (
                <div className="p-3.5 bg-slate-50 border border-slate-200 rounded-xl space-y-3">
                  <div className="flex items-center justify-between pb-2 border-b border-slate-200">
                    <div>
                      <span className="text-xs font-bold text-gray-900 block">Buscar Nova Clínica Credenciada</span>
                      <span className="text-[11px] text-gray-500">Selecione o estado, cidade e bairro para filtrar as unidades</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setAlterandoClinica(false)}
                      className="text-xs text-gray-600 hover:text-gray-900 font-medium px-2 py-1 rounded bg-white border border-gray-200"
                    >
                      Cancelar
                    </button>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                    {/* Estado */}
                    <div>
                      <label className="block text-[11px] font-semibold text-gray-700 mb-1">
                        Estado (UF) <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={filtroEstado}
                        onChange={(e) => handleSelecionarEstado(e.target.value)}
                        disabled={carregandoEstados}
                        className="w-full px-2.5 py-1.5 bg-white border border-gray-300 rounded-lg text-xs text-gray-900 focus:ring-1 focus:ring-primary-500 focus:outline-none"
                      >
                        <option value="">{carregandoEstados ? 'Carregando...' : 'Selecione o estado...'}</option>
                        {estadosList.map((uf) => (
                          <option key={uf.uf || uf.nome} value={uf.uf || uf.nome}>
                            {uf.nome || uf.uf} ({uf.uf})
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Cidade */}
                    <div>
                      <label className="block text-[11px] font-semibold text-gray-700 mb-1">
                        Cidade <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={filtroCidade}
                        onChange={(e) => handleSelecionarCidade(e.target.value)}
                        disabled={!filtroEstado || carregandoCidades}
                        className="w-full px-2.5 py-1.5 bg-white border border-gray-300 rounded-lg text-xs text-gray-900 focus:ring-1 focus:ring-primary-500 focus:outline-none disabled:bg-gray-100 disabled:text-gray-400"
                      >
                        <option value="">{carregandoCidades ? 'Carregando cidades...' : 'Selecione a cidade...'}</option>
                        {cidadesList.map((cid) => (
                          <option key={cid.nome || cid.cidade} value={cid.nome || cid.cidade}>
                            {cid.nome || cid.cidade}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Bairro */}
                    <div>
                      <label className="block text-[11px] font-semibold text-gray-700 mb-1">
                        Bairro
                      </label>
                      <select
                        value={filtroBairro}
                        onChange={(e) => handleSelecionarBairro(e.target.value)}
                        disabled={!filtroCidade || carregandoBairros}
                        className="w-full px-2.5 py-1.5 bg-white border border-gray-300 rounded-lg text-xs text-gray-900 focus:ring-1 focus:ring-primary-500 focus:outline-none disabled:bg-gray-100 disabled:text-gray-400"
                      >
                        <option value="">Todos os bairros</option>
                        {bairrosList.map((b) => (
                          <option key={b.nome} value={b.nome}>
                            {b.nome}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Lista de Clínicas Filtradas */}
                  <div className="pt-2 border-t border-slate-200">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[11px] font-bold text-gray-700 uppercase tracking-wider">
                        Clínicas Encontradas {clinicasFiltradasList.length > 0 && `(${clinicasFiltradasList.length})`}
                      </span>
                      {carregandoClinicasFiltradas && (
                        <span className="text-[11px] text-gray-500 flex items-center gap-1">
                          <Loader2 className="w-3 h-3 animate-spin text-primary-600" /> Buscando clínicas...
                        </span>
                      )}
                    </div>

                    {!filtroEstado ? (
                      <p className="text-xs text-gray-500 italic py-3 text-center bg-white rounded-lg border border-dashed border-gray-200">
                        Selecione o estado para visualizar as clínicas credenciadas.
                      </p>
                    ) : carregandoClinicasFiltradas ? (
                      <div className="py-4 text-center text-xs text-gray-500 flex items-center justify-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin text-primary-600" />
                        Carregando clínicas credenciadas...
                      </div>
                    ) : clinicasFiltradasList.length === 0 ? (
                      <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3 text-center">
                        Nenhuma clínica credenciada encontrada para os filtros selecionados.
                      </p>
                    ) : (
                      <div className="max-h-48 overflow-y-auto space-y-1.5 pr-1">
                        {clinicasFiltradasList.map((clinica) => {
                          const isSelected = Number(novaClinicaReagendamento) === Number(clinica.id);
                          return (
                            <div
                              key={clinica.id}
                              onClick={() => handleEscolherClinica(clinica)}
                              className={`p-2.5 rounded-lg border cursor-pointer transition-all flex items-center justify-between gap-2 text-xs ${
                                isSelected
                                  ? 'bg-primary-50 border-primary-500 ring-1 ring-primary-500 text-primary-900 font-semibold'
                                  : 'bg-white border-gray-200 hover:border-primary-300 hover:bg-slate-50 text-gray-800'
                              }`}
                            >
                              <div className="min-w-0 flex-1">
                                <p className="font-semibold truncate">{clinica.nome}</p>
                                <p className="text-[11px] text-gray-500 truncate">
                                  {[clinica.bairro, clinica.cidade, clinica.uf].filter(Boolean).join(' - ') || clinica.endereco}
                                </p>
                              </div>
                              <button
                                type="button"
                                className={`px-2.5 py-1 text-xs rounded font-medium shrink-0 ${
                                  isSelected
                                    ? 'bg-primary-600 text-white'
                                    : 'bg-gray-100 hover:bg-primary-50 text-gray-700 hover:text-primary-700 border border-gray-200'
                                }`}
                              >
                                {isSelected ? 'Selecionada' : 'Selecionar'}
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Checklist de Exames */}
            {itensReagendamento.length > 1 ? (
              <div className="p-3 bg-slate-50/90 rounded-xl border border-slate-200 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-bold text-slate-700 uppercase tracking-wider block">
                    Exames a Reagendar ({itensSelecionadosReagendar.length} de {itensReagendamento.length}):
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      if (itensSelecionadosReagendar.length === itensReagendamento.length) {
                        setItensSelecionadosReagendar([]);
                        setTipoReagendamento('parcial');
                      } else {
                        setItensSelecionadosReagendar(itensReagendamento.map((i) => i.id));
                        setTipoReagendamento('total');
                      }
                    }}
                    className="text-[11px] font-semibold text-indigo-600 hover:text-indigo-800 transition cursor-pointer"
                  >
                    {itensSelecionadosReagendar.length === itensReagendamento.length
                      ? 'Desmarcar todos'
                      : 'Selecionar todos'}
                  </button>
                </div>

                {carregandoItensReagendamento ? (
                  <div className="py-3 text-center text-xs text-indigo-600">
                    <RefreshCw className="w-4 h-4 animate-spin inline mr-1" />
                    Carregando exames do agendamento...
                  </div>
                ) : (
                  <div className="space-y-1.5 max-h-52 overflow-y-auto p-0.5">
                    {itensReagendamento.map((itemExame) => {
                      const isChecked = itensSelecionadosReagendar.includes(itemExame.id);
                      return (
                        <label
                          key={itemExame.id}
                          className={`flex items-start gap-2.5 p-2 rounded-lg border text-xs cursor-pointer transition-all ${
                            isChecked
                              ? 'bg-indigo-50/70 border-indigo-300 font-semibold text-indigo-950 shadow-2xs'
                              : 'bg-white border-slate-200 hover:bg-slate-50 text-gray-500'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={(e) => {
                              if (e.target.checked) {
                                const novos = [...itensSelecionadosReagendar, itemExame.id];
                                setItensSelecionadosReagendar(novos);
                                if (novos.length === itensReagendamento.length) {
                                  setTipoReagendamento('total');
                                }
                              } else {
                                const novos = itensSelecionadosReagendar.filter((id) => id !== itemExame.id);
                                setItensSelecionadosReagendar(novos);
                                setTipoReagendamento('parcial');
                              }
                            }}
                            className="mt-0.5 rounded text-indigo-600 focus:ring-indigo-500"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-1.5">
                              <span className="block truncate font-semibold">
                                {formatarNomeExameAmigavel(itemExame.exame_nome || (itemExame as { nome?: string }).nome)}
                              </span>
                              <span className="px-1.5 py-0.2 rounded text-[9px] font-bold shrink-0 bg-slate-100 text-slate-700 border border-slate-200">
                                {itemExame.is_clinico ? 'Exame Clínico' : 'Complementar'}
                              </span>
                            </div>
                            <span className="text-[10px] text-gray-400 font-normal">
                              Status: {itemExame.status || 'PENDENTE'}
                            </span>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            ) : (
              /* Se for apenas 1 exame: não precisa perguntar se é total ou parcial, apenas informa o exame */
              <div className="p-3 bg-slate-50 rounded-xl border border-slate-200 text-xs">
                <span className="text-[11px] font-bold text-slate-600 uppercase tracking-wider block mb-1">
                  Exame a Reagendar:
                </span>
                <div className="p-2 bg-white rounded-lg border border-slate-200 text-xs text-gray-800 font-medium">
                  {itensReagendamento.length === 1
                    ? formatarNomeExameAmigavel(itensReagendamento[0].exame_nome || (itensReagendamento[0] as { exame_nome?: string; nome?: string }).nome)
                    : formatarNomeExameAmigavel(reagendandoItem.exame_nome || 'Exame Ocupacional')}
                </div>
              </div>
            )}

            {/* Motivo se for parcial */}
            {tipoReagendamento === 'parcial' && itensSelecionadosReagendar.length < itensReagendamento.length && (
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Motivo do Sub-agendamento / Recoleta (opcional)
                </label>
                <input
                  type="text"
                  value={motivoReagendamento}
                  onChange={(e) => setMotivoReagendamento(e.target.value)}
                  placeholder="Ex: Não compareceu para coleta de sangue / recoleta..."
                  className="w-full text-xs border border-gray-300 rounded-xl p-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                />
              </div>
            )}

            {/* CAMPO ÚNICO DE DATA COM ATALHOS / CHIPS DE SUGESTÃO REGULAR */}
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Data do Agendamento <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                required
                min={getDeslocamentoDiasISO(0)}
                value={novaDataReagendamento}
                onChange={(e) => setNovaDataReagendamento(e.target.value)}
                className="w-full text-xs border border-gray-300 rounded-xl p-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
              />

              {loadingDatasReagendar ? (
                <div className="mt-1.5 flex items-center gap-1.5 text-[11px] text-indigo-600">
                  <RefreshCw className="w-3 h-3 animate-spin" />
                  <span>Consultando calendário e agenda da clínica...</span>
                </div>
              ) : datasDisponiveisReagendar.length > 0 ? (
                <div className="mt-2 space-y-1">
                  <span className="text-[10px] text-gray-500 font-medium block">
                    Datas regulares com atendimento na clínica (clique para preencher):
                  </span>
                  <div className="flex flex-wrap gap-1.5">
                    {datasDisponiveisReagendar
                      .filter((dateStr) => dateStr >= getDeslocamentoDiasISO(0))
                      .slice(0, 6)
                      .map((dateStr) => {
                        const isSelected = novaDataReagendamento === dateStr;
                        return (
                          <button
                            key={dateStr}
                            type="button"
                            onClick={() => setNovaDataReagendamento(dateStr)}
                            className={`px-2.5 py-1 rounded-lg text-[11px] font-medium border transition cursor-pointer ${
                              isSelected
                                ? 'bg-indigo-600 text-white border-indigo-600 font-bold shadow-2xs'
                                : 'bg-indigo-50/50 text-indigo-800 border-indigo-200 hover:bg-indigo-100 hover:border-indigo-300'
                            }`}
                          >
                            {formatDateBR(dateStr)}
                          </button>
                        );
                      })}
                  </div>
                </div>
              ) : null}

              {datasDisponiveisReagendar.length > 0 &&
                novaDataReagendamento &&
                !datasDisponiveisReagendar.includes(novaDataReagendamento) && (
                  <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-1.5 text-[11px] text-amber-800">
                    <AlertTriangle className="w-3.5 h-3.5 text-amber-600 shrink-0 mt-0.5" />
                    <span>
                      Atenção: A data selecionada pode estar fora do expediente normal da clínica ou em período de recesso. Recomendamos escolher uma das opções sugeridas acima.
                    </span>
                  </div>
                )}
            </div>

            <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-100">
              <button
                onClick={() => setReagendandoItem(null)}
                disabled={actionLoading}
                className="px-4 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-100 rounded-xl transition-colors cursor-pointer"
              >
                Voltar
              </button>
              <button
                onClick={handleConfirmReagendar}
                disabled={actionLoading || !novaDataReagendamento}
                className="px-4 py-2 text-xs font-bold text-white bg-indigo-600 hover:bg-indigo-700 rounded-xl shadow-sm transition-colors disabled:opacity-50 cursor-pointer"
              >
                {actionLoading
                  ? 'Salvando...'
                  : tipoReagendamento === 'parcial'
                  ? 'Confirmar Sub-agendamento'
                  : 'Confirmar Nova Data'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================= */}
      {/* MODAL: AUDITORIA DO HISTÓRICO DE STATUS DO AGENDAMENTO    */}
      {/* ========================================================= */}
      {historicoAuditItem && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-2xl space-y-4 animate-in fade-in zoom-in duration-150 max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between pb-3 border-b border-gray-100 flex-shrink-0">
              <div className="flex items-center gap-2 text-slate-800">
                <History className="w-5 h-5 text-sky-600" />
                <div>
                  <h3 className="text-base font-bold text-gray-900">Histórico de Status</h3>
                  <p className="text-xs text-gray-500">
                    {historicoAuditItem.colaborador_nome} • {historicoAuditItem.exame_nome}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setHistoricoAuditItem(null)}
                className="text-gray-400 hover:text-gray-600 p-1 rounded-lg"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto pr-1">
              {loadingHistoricoStatus ? (
                <div className="flex flex-col items-center justify-center py-8 gap-2">
                  <RefreshCw className="w-6 h-6 text-sky-600 animate-spin" />
                  <span className="text-xs text-slate-500">Carregando histórico...</span>
                </div>
              ) : historicoStatusLista.length === 0 ? (
                <div className="text-center py-8 text-slate-500 text-xs">
                  Nenhuma alteração de status registrada para este agendamento.
                </div>
              ) : (
                <div className="space-y-4 relative before:absolute before:inset-0 before:left-3.5 before:w-0.5 before:bg-slate-200">
                  {historicoStatusLista.map((h, idx) => (
                    <div key={h.id || idx} className="relative flex items-start gap-3 pl-8">
                      <div className="absolute left-2 top-1.5 w-3.5 h-3.5 rounded-full bg-sky-500 border-2 border-white shadow-xs -translate-x-1/2"></div>
                      <div className="bg-slate-50 rounded-xl p-3 border border-slate-200 flex-1 space-y-1.5 text-xs">
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            {h.status_anterior ? (
                              <>
                                <span className="font-semibold text-slate-600">
                                  {h.status_anterior.descricao}
                                </span>
                                <span className="text-slate-400">→</span>
                              </>
                            ) : null}
                            <span className="font-bold text-sky-700 bg-sky-100 px-2 py-0.5 rounded-md">
                              {h.status_novo.descricao}
                            </span>
                          </div>
                          <span
                            className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                              h.origem === 'colaborador'
                                ? 'bg-emerald-100 text-emerald-700'
                                : h.origem === 'clinica'
                                ? 'bg-purple-100 text-purple-700'
                                : h.origem === 'rh'
                                ? 'bg-indigo-100 text-indigo-700'
                                : 'bg-slate-200 text-slate-700'
                            }`}
                          >
                            {h.origem.toUpperCase()}
                          </span>
                        </div>

                        {h.observacao && (
                          <p className="text-slate-600 text-[11px] leading-relaxed">
                            {h.observacao}
                          </p>
                        )}

                        <div className="text-[10px] text-slate-400 pt-1 border-t border-slate-200/60">
                          {new Date(h.created_at).toLocaleString('pt-BR')}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex items-center justify-end pt-3 border-t border-gray-100 flex-shrink-0">
              <button
                onClick={() => setHistoricoAuditItem(null)}
                className="px-4 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-100 rounded-xl transition-colors"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================= */}
      {/* MODAL: COMPROVANTE DE PRESENÇA OU CONFIRMAÇÃO             */}
      {/* ========================================================= */}
      {selectedComprovante && (() => {
        const isModalPresenca = Boolean(selectedComprovante.confirmou_presenca || selectedComprovante.status === 'Presente' || selectedComprovante.status === 'Compareceu');
        return (
          <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-xs flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl space-y-4 animate-in fade-in zoom-in-95 duration-150 border border-slate-200">
              <div className="flex items-start justify-between pb-3 border-b border-gray-100">
                <div className="flex items-center gap-2.5">
                  <div
                    className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                      isModalPresenca
                        ? 'bg-emerald-100 text-emerald-700'
                        : 'bg-blue-100 text-blue-700'
                    }`}
                  >
                    {isModalPresenca ? (
                      <ShieldCheck className="w-6 h-6" />
                    ) : (
                      <CheckCircle2 className="w-6 h-6" />
                    )}
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-gray-900 leading-tight">
                      {isModalPresenca
                        ? 'Comprovante de Presença'
                        : 'Confirmação de Comparecimento'}
                    </h3>
                    <span className="text-xs text-gray-500">
                      {isModalPresenca
                        ? 'Presença e identidade verificadas'
                        : 'Intenção confirmada pelo colaborador'}
                    </span>
                  </div>
                </div>
              <button
                type="button"
                onClick={() => setSelectedComprovante(null)}
                className="text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
              >
                ✕
              </button>
            </div>

            {/* Informações do Agendamento */}
            <div className="bg-slate-50 p-3.5 rounded-xl border border-slate-200 text-xs space-y-2">
              <div className="flex items-start justify-between">
                <span className="text-gray-500 font-semibold">Colaborador:</span>
                <span className="font-bold text-gray-900 text-right">{selectedComprovante.colaborador_nome}</span>
              </div>
              {selectedComprovante.colaborador_cpf && (
                <div className="flex items-start justify-between">
                  <span className="text-gray-500 font-semibold">CPF:</span>
                  <span className="font-medium text-gray-800">{formatCPF(selectedComprovante.colaborador_cpf)}</span>
                </div>
              )}
              <div className="flex items-start justify-between">
                <span className="text-gray-500 font-semibold">Exame:</span>
                <span className="font-bold text-gray-900 text-right">{selectedComprovante.exame_nome}</span>
              </div>
              <div className="flex items-start justify-between">
                <span className="text-gray-500 font-semibold">Data do Agendamento:</span>
                <span className="font-bold text-gray-900">{formatDateBR(selectedComprovante.data_agendamento)}</span>
              </div>
              {selectedComprovante.confirmou_presenca && (
                <div className="flex items-start justify-between">
                  <span className="text-gray-500 font-semibold">Horário Registrado:</span>
                  <span className="font-bold text-emerald-800">
                    {selectedComprovante.presenca_horario || 'Informado'}
                    {selectedComprovante.tipo_confirmacao ? ` (${selectedComprovante.tipo_confirmacao === 'ja_chegou' ? 'Já na clínica' : 'Horário previsto'})` : ''}
                  </span>
                </div>
              )}
              {selectedComprovante.clinica_nome && (
                <div className="flex items-start justify-between pt-1 border-t border-slate-200/70">
                  <span className="text-gray-500 font-semibold">Clínica:</span>
                  <span className="font-bold text-gray-900 text-right max-w-[220px]">{selectedComprovante.clinica_nome}</span>
                </div>
              )}
              {selectedComprovante.confirmou_presenca && selectedComprovante.presenca_em && (
                <div className="flex items-start justify-between pt-1 border-t border-slate-200/70">
                  <span className="text-gray-500 font-semibold">Presença confirmada em:</span>
                  <span className="font-medium text-gray-700">{formatDateTimeBR(selectedComprovante.presenca_em)}</span>
                </div>
              )}
              {!selectedComprovante.confirmou_presenca && selectedComprovante.intencao_em && (
                <div className="flex items-start justify-between pt-1 border-t border-slate-200/70">
                  <span className="text-gray-500 font-semibold">Intenção confirmada em:</span>
                  <span className="font-medium text-gray-700">{formatDateTimeBR(selectedComprovante.intencao_em)}</span>
                </div>
              )}
            </div>

            {/* Foto / Selfie de Comprovação OU Card Informativo de Intenção */}
            {selectedComprovante.confirmou_presenca ? (
              selectedComprovante.selfie_path ? (
                <div className="text-center pt-1">
                  <label className="block text-xs font-semibold text-gray-700 mb-2">
                    Foto registrada para auditoria:
                  </label>
                  <div className="relative w-44 h-44 rounded-2xl mx-auto border-2 border-emerald-400 shadow-md bg-slate-50 flex items-center justify-center overflow-hidden">
                    {loadingSelfie ? (
                      <div className="flex flex-col items-center gap-1.5 text-gray-400">
                        <RefreshCw className="w-6 h-6 animate-spin text-emerald-600" />
                        <span className="text-[11px] text-gray-500 font-medium">Carregando foto...</span>
                      </div>
                    ) : selfieBlobUrl ? (
                      <img
                        src={selfieBlobUrl}
                        alt="Selfie de Confirmação"
                        className="w-full h-full object-cover"
                        onError={() => setSelfieError(true)}
                      />
                    ) : selfieError ? (
                      <div className="p-3 text-center text-gray-400">
                        <AlertTriangle className="w-6 h-6 mx-auto mb-1 text-amber-500" />
                        <span className="text-[11px] text-gray-500">Selfie não pôde ser carregada</span>
                      </div>
                    ) : (
                      <div className="p-3 text-center text-gray-400">
                        <CheckCircle2 className="w-6 h-6 mx-auto mb-1 text-emerald-500" />
                        <span className="text-[11px] text-gray-500">Presença validada</span>
                      </div>
                    )}
                  </div>
                  <span className="text-[11px] text-gray-500 mt-1 block">
                    Identidade verificada pelo colaborador
                  </span>
                </div>
              ) : (
                <div className="p-3 bg-emerald-50 rounded-xl border border-emerald-200 text-xs text-emerald-800 flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                  <span>Presença registrada com sucesso no sistema SST Exames.</span>
                </div>
              )
            ) : (
              <div className="p-3.5 bg-blue-50 rounded-xl border border-blue-200 text-xs text-blue-900 flex items-start gap-2.5">
                <CheckCircle2 className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
                <div className="space-y-0.5">
                  <p className="font-bold">Colaborador confirmou que irá ao exame</p>
                  <p className="text-[11px] text-blue-700">
                    O colaborador confirmou previamente sua presença para a realização dos exames na clínica e data agendadas. A verificação presencial definitiva com selfie e registro de horário é realizada no dia do exame.
                  </p>
                </div>
              </div>
            )}

            <div className="pt-2">
              <button
                type="button"
                onClick={() => setSelectedComprovante(null)}
                className={`w-full py-2.5 px-4 text-xs font-bold rounded-xl transition-colors cursor-pointer text-white shadow-xs ${
                  isModalPresenca
                    ? 'bg-emerald-600 hover:bg-emerald-700'
                    : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      );
    })()}

      {/* Modal de Reagendamento de Autoagendamento (Link Expirado ou Renovação de Prazo) */}
      {reagendandoExpiradoItem && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-2xl space-y-5 animate-in fade-in zoom-in duration-200">
            {/* Cabeçalho */}
            <div className="flex items-center justify-between pb-3 border-b border-gray-100">
              <div className="flex items-center gap-2.5">
                <div className="p-2 bg-indigo-50 text-indigo-700 rounded-xl border border-indigo-200">
                  <Calendar className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-gray-900">Reagendar Autoagendamento</h3>
                  <p className="text-xs text-gray-500">Defina uma nova data limite para o colaborador escolher a clínica</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setReagendandoExpiradoItem(null)}
                className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Informações do Agendamento */}
            <div className="bg-gray-50 rounded-xl p-3.5 border border-gray-200 text-xs space-y-1.5">
              <div className="flex items-center justify-between">
                <span className="text-gray-500 font-medium">Colaborador:</span>
                <span className="font-bold text-gray-900">{reagendandoExpiradoItem.colaborador_nome}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-500 font-medium">CPF:</span>
                <span className="font-mono text-gray-700">{formatCPF(reagendandoExpiradoItem.colaborador_cpf)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-500 font-medium">Exame:</span>
                <span className="font-semibold text-emerald-800 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
                  {reagendandoExpiradoItem.exame_nome}
                </span>
              </div>
              {reagendandoExpiradoItem.data_agendamento && (
                <div className="flex items-center justify-between">
                  <span className="text-gray-500 font-medium">Data Anterior / Expirada:</span>
                  <span className="text-rose-700 font-semibold line-through">
                    {formatDateBR(reagendandoExpiradoItem.data_agendamento)}
                  </span>
                </div>
              )}
            </div>

            {/* Formulário: Nova Data Limite e Motivo */}
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">
                  Nova Data Limite para Escolha da Clínica <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  min={new Date().toISOString().split('T')[0]}
                  value={novaDataExpirado}
                  onChange={(e) => setNovaDataExpirado(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white"
                  required
                />
                <p className="text-[11px] text-gray-500 mt-1">
                  O colaborador terá até esta data para acessar o link e selecionar a clínica desejada.
                </p>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Motivo do Reagendamento <span className="text-gray-400 font-normal">(Opcional)</span>
                </label>
                <input
                  type="text"
                  placeholder="Ex: Prazo anterior expirado, colaborador solicitou prorrogação"
                  value={motivoExpirado}
                  onChange={(e) => setMotivoExpirado(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white"
                />
              </div>
            </div>

            {/* Aviso Obrigatório do Caso de Uso */}
            <div className="bg-sky-50 border border-sky-200 rounded-xl p-3.5 flex items-start gap-3">
              <Info className="w-5 h-5 text-sky-600 shrink-0 mt-0.5" />
              <div className="text-xs text-sky-900 space-y-1">
                <p className="font-semibold text-sky-950">Disparo Automático de Convite ao Colaborador</p>
                <p className="text-sky-800 leading-relaxed">
                  Ao confirmar o reagendamento, o link será renovado e o colaborador <strong>receberá automaticamente uma nova notificação por E-mail e WhatsApp</strong> informando a nova data limite para escolha da clínica.
                </p>
                <p className="text-sky-700 text-[11px] leading-relaxed">
                  <em>Nota:</em> Este aviso de marcação é disparado para todos os tipos de exame (inclusive Demissional). Apenas os lembretes automáticos diários subsequentes respeitarão a política anti-spam (sem lembretes diários para demissionais).
                </p>
              </div>
            </div>

            {/* Rodapé / Ações */}
            <div className="flex items-center justify-end gap-2.5 pt-2 border-t border-gray-100">
              <button
                type="button"
                onClick={() => setReagendandoExpiradoItem(null)}
                disabled={loadingReagendarExpirado}
                className="px-4 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-100 rounded-xl transition-colors"
              >
                Voltar
              </button>
              <button
                type="button"
                onClick={handleExecutarReagendarExpirado}
                disabled={loadingReagendarExpirado || !novaDataExpirado}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-bold text-white bg-indigo-600 hover:bg-indigo-700 rounded-xl transition-colors shadow-xs disabled:opacity-50"
              >
                {loadingReagendarExpirado ? (
                  <>
                    <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                    <span>Reagendando...</span>
                  </>
                ) : (
                  <>
                    <Calendar className="w-3.5 h-3.5" />
                    <span>Confirmar e Reagendar</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
